import React, { useState, useEffect } from 'react';

const Special: React.FC = () => {
  // State for FAQ accordion
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Reusable CTA Component
  const CTASection = ({ 
    className = "", 
    showHeadline = true, 
    showRating = true, 
    showPaymentImage = true,
    buttonText = "Get BrandScapes AI Right Now!",
    headlineText = "Grab Access To BrandScapes AI Today...",
    animationDelay = "",
    useDarkPaymentImage = false,
    darkMode = false
  }) => (
    <div className={`max-w-xl mx-auto ${className}`}>
      {showHeadline && (
        <>
          {/* CTA Headline */}
          <h3 className={`text-xl md:text-2xl font-bold text-center mb-2 ${darkMode ? 'text-gray-900' : 'text-white'}`}>
            {headlineText}
          </h3>
          <div className="text-center mb-6">
            <span className={`text-base ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>No Monthly Fees – </span>
            <span className={`text-base font-semibold relative ${darkMode ? 'text-gray-900' : 'text-white'}`}>
              One Time Payment
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></span>
            </span>
          </div>
        </>
      )}

      {showRating && (
        <>
          {/* Customer Avatars & Rating */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex">
              <img 
                src="/peopletop.png" 
                alt="Happy customers" 
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-1">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className={`font-semibold ${darkMode ? 'text-gray-900' : 'text-white'}`}>4.9</span>
            </div>
          </div>
          <p className={`text-center text-xs mb-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>From 3,000+ reviews</p>
        </>
      )}

      {/* Main CTA Button - Enhanced Design */}
      <div className={`max-w-md mx-auto mb-6`}>
        <button 
          onClick={() => scrollToSection('pricing')}
          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white text-lg font-bold py-4 px-6 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02] shadow-xl"
        >
          {buttonText}
        </button>
      </div>

      {showPaymentImage && (
        /* Payment Methods & Guarantee Image */
        <div className="flex justify-center mb-16">
          <img 
            src={(useDarkPaymentImage || darkMode) ? "/payment-moneyback-dark.png" : "/payment-moneyback.webp"} 
            alt="Payment methods and money back guarantee" 
            className="max-w-full h-auto"
          />
        </div>
      )}
    </div>
  );

  // Add CSS animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes grid-flow {
        0% { transform: perspective(1000px) rotateX(60deg) scale(2) translateX(0); }
        100% { transform: perspective(1000px) rotateX(60deg) scale(2) translateX(60px); }
      }
      
      @keyframes flow-smooth {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      
      @keyframes float-up {
        0% { 
          transform: translateY(0) scale(0);
          opacity: 0;
        }
        10% {
          transform: translateY(-10px) scale(1);
          opacity: 1;
        }
        90% {
          transform: translateY(-200px) scale(1);
          opacity: 1;
        }
        100% { 
          transform: translateY(-250px) scale(0);
          opacity: 0;
        }
      }
      
      @keyframes draw-line {
        0% { stroke-dasharray: 0 200; }
        100% { stroke-dasharray: 200 0; }
      }
      
      @keyframes draw-check {
        0% { stroke-dasharray: 0 50; }
        100% { stroke-dasharray: 50 0; }
      }
      
      @keyframes gradient-x {
        0%, 100% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
      }
      
      @keyframes scale-in {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      
      @keyframes fade-in-up {
        0% {
          transform: translateY(20px);
          opacity: 0;
        }
        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes fade-in-down {
        0% {
          transform: translateY(-20px);
          opacity: 0;
        }
        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes fade-in-left {
        0% {
          transform: translateX(-20px);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes fade-in-right {
        0% {
          transform: translateX(20px);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes float {
        0%, 100% { transform: translateY(0) scale(1); }
        50% { transform: translateY(-20px) scale(1.05); }
      }
      
      @keyframes ping {
        75%, 100% {
          transform: scale(2);
          opacity: 0;
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: .5;
        }
      }
      
      @keyframes subtle-shift {
        0%, 100% {
          transform: translateX(0) translateY(0);
        }
        50% {
          transform: translateX(30px) translateY(-30px);
        }
      }
      
      @keyframes rotate-y-5 {
        from { transform: rotateY(0deg); }
        to { transform: rotateY(5deg); }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      .animate-grid-flow {
        animation: grid-flow 30s linear infinite;
      }
      
      .animate-flow-smooth {
        animation: flow-smooth 8s linear infinite;
      }
      
      .animate-float-up {
        animation: float-up 10s linear infinite;
      }
      
      .animate-draw-line {
        animation: draw-line 1s ease-out forwards;
      }
      
      .animate-draw-check {
        animation: draw-check 0.5s ease-out forwards;
        stroke-dasharray: 0 50;
      }
      
      .animate-gradient-x {
        background-size: 200% 200%;
        animation: gradient-x 3s ease infinite;
      }
      
      .animate-scale-in {
        animation: scale-in 0.5s ease-out forwards;
      }
      
      .animate-fade-in-up {
        animation: fade-in-up 0.6s ease-out forwards;
      }
      
      .animate-fade-in-down {
        animation: fade-in-down 0.6s ease-out forwards;
      }
      
      .animate-fade-in-left {
        animation: fade-in-left 0.6s ease-out forwards;
      }
      
      .animate-fade-in-right {
        animation: fade-in-right 0.6s ease-out forwards;
      }
      
      .animate-spin-slow {
        animation: spin-slow 10s linear infinite;
      }
      
      .animate-float {
        animation: float 6s ease-in-out infinite;
      }
      
      .animate-ping {
        animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
      }
      
      .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
      
      .animate-subtle-shift {
        animation: subtle-shift 20s ease-in-out infinite;
      }
      
      .transform-gpu {
        transform: translateZ(0);
      }
      
      .animation-delay-200 { animation-delay: 200ms; }
      .animation-delay-400 { animation-delay: 400ms; }
      .animation-delay-600 { animation-delay: 600ms; }
      .animation-delay-800 { animation-delay: 800ms; }
      .animation-delay-1000 { animation-delay: 1000ms; }
      .animation-delay-1200 { animation-delay: 1200ms; }
      .animation-delay-1400 { animation-delay: 1400ms; }
      .animation-delay-1600 { animation-delay: 1600ms; }
      .animation-delay-1800 { animation-delay: 1800ms; }
      .animation-delay-2000 { animation-delay: 2000ms; }
      .animation-delay-4000 { animation-delay: 4000ms; }
      
      @keyframes cardCycle1 {
        0%, 25% { 
          transform: rotate(0deg) scale(1) translateX(0) translateY(0);
          opacity: 1;
          z-index: 30;
        }
        30%, 95% { 
          transform: rotate(3deg) scale(0.9) translateX(20px) translateY(10px);
          opacity: 0.7;
          z-index: 20;
        }
        100% { 
          transform: rotate(0deg) scale(1) translateX(0) translateY(0);
          opacity: 1;
          z-index: 30;
        }
      }
      
      @keyframes cardCycle2 {
        0%, 20% { 
          transform: rotate(3deg) scale(0.9) translateX(20px) translateY(10px);
          opacity: 0.7;
          z-index: 20;
        }
        25%, 50% { 
          transform: rotate(0deg) scale(1) translateX(0) translateY(0);
          opacity: 1;
          z-index: 30;
        }
        55%, 95% { 
          transform: rotate(-3deg) scale(0.85) translateX(-15px) translateY(15px);
          opacity: 0.5;
          z-index: 10;
        }
        100% { 
          transform: rotate(3deg) scale(0.9) translateX(20px) translateY(10px);
          opacity: 0.7;
          z-index: 20;
        }
      }
      
      @keyframes cardCycle3 {
        0%, 45% { 
          transform: rotate(-3deg) scale(0.85) translateX(-15px) translateY(15px);
          opacity: 0.5;
          z-index: 10;
        }
        50%, 75% { 
          transform: rotate(0deg) scale(1) translateX(0) translateY(0);
          opacity: 1;
          z-index: 30;
        }
        80%, 95% { 
          transform: rotate(2deg) scale(0.8) translateX(25px) translateY(-10px);
          opacity: 0.3;
          z-index: 5;
        }
        100% { 
          transform: rotate(-3deg) scale(0.85) translateX(-15px) translateY(15px);
          opacity: 0.5;
          z-index: 10;
        }
      }
      
      @keyframes cardCycle4 {
        0%, 70% { 
          transform: rotate(2deg) scale(0.8) translateX(25px) translateY(-10px);
          opacity: 0.3;
          z-index: 5;
        }
        75%, 100% { 
          transform: rotate(0deg) scale(1) translateX(0) translateY(0);
          opacity: 1;
          z-index: 30;
        }
      }
      
      @keyframes indicatorCycle1 {
        0%, 25% { 
          background-color: #3b82f6;
          transform: scale(1.2);
        }
        30%, 100% { 
          background-color: #94a3b8;
          transform: scale(1);
        }
      }
      
      @keyframes indicatorCycle2 {
        0%, 20% { 
          background-color: #94a3b8;
          transform: scale(1);
        }
        25%, 50% { 
          background-color: #a855f7;
          transform: scale(1.2);
        }
        55%, 100% { 
          background-color: #94a3b8;
          transform: scale(1);
        }
      }
      
      @keyframes indicatorCycle3 {
        0%, 45% { 
          background-color: #94a3b8;
          transform: scale(1);
        }
        50%, 75% { 
          background-color: #f97316;
          transform: scale(1.2);
        }
        80%, 100% { 
          background-color: #94a3b8;
          transform: scale(1);
        }
      }
      
      @keyframes indicatorCycle4 {
        0%, 70% { 
          background-color: #94a3b8;
          transform: scale(1);
        }
        75%, 100% { 
          background-color: #22c55e;
          transform: scale(1.2);
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 23,
    minutes: 45,
    seconds: 12
  });

  // Interactive countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* HERO SECTION - Premium Dark Design */}
      <div className="relative min-h-[100vh] lg:min-h-[90vh] overflow-hidden bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* DarkVeil animated background overlay */}
        <div className="dark-veil"></div>
        {/* Animated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-pink-900/20"></div>
          <div 
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgb(99 102 241 / 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          ></div>
        </div>
        
        {/* Gradient Orbs - Dark Theme */}
        <div className="absolute top-20 -left-20 w-72 h-72 bg-purple-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 -right-20 w-72 h-72 bg-blue-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-40 w-72 h-72 bg-pink-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-12 hero-content">
          <div className="text-center">
            {/* BrandScapes AI Logo - Smaller */}
            <div className="mb-6 animate-fade-in-down">
              <img 
                src="/clawbrand_logo_color.png" 
                alt="ClawBrand AI" 
                className="h-12 md:h-14 mx-auto brightness-110 contrast-125"
              />
            </div>

            {/* Subheadline with gradient */}
            <div className="mb-4 animate-fade-in-up animation-delay-200">
              <div className="flex items-center justify-center space-x-3 text-gray-400 mb-3">
                <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-gray-600"></div>
                <span className="text-xs uppercase tracking-widest font-medium">AI-Powered Design Studio</span>
                <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-gray-600"></div>
              </div>
              <h2 className="text-lg md:text-xl font-medium text-gray-300">
                Create Photorealistic Product Mockups In 30 Seconds
              </h2>
            </div>
            
            {/* Main Headline - Bold 2 rows */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-6 leading-[1.1] animate-fade-in-up animation-delay-400 max-w-6xl mx-auto" style={{ fontWeight: 900 }}>
              <span className="text-white font-black">Turn Any Design Into </span>
              <span className="relative inline-block">
                <span className="relative z-10 gradient-text-spectrum font-black">
                  Stunning Visuals
                </span>
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-gradient-to-r from-purple-600/30 via-blue-600/30 to-purple-600/30 blur-sm"></span>
              </span>
              <br />
              <span className="text-white font-black">For Your </span>
              <span className="gradient-text-warm font-black">
                Funnels, Ads & Launches
              </span>
            </h1>
            
            {/* Supporting text - Shorter */}
            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed animate-fade-in-up animation-delay-600">
              Stop wasting time and money on designers. Upload any logo, product, or course design 
              and get unlimited studio-quality mockups instantly.
            </p>

            {/* Vimeo Video - Enhanced Design */}
            <div className="mb-8 animate-fade-in-up animation-delay-800">
              <div className="relative max-w-4xl mx-auto">
                {/* Glow effect around video */}
                <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-blue-500 to-purple-500 rounded-2xl opacity-30 blur-2xl animate-pulse"></div>
                
                {/* Video container */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-600/50 bg-black">
                  <div className="aspect-video">
                    <iframe
                      src="https://player.vimeo.com/video/1121845662?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=0&loop=0&muted=0"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                      title="BrandScapes AI Demo"
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  
                  {/* Video overlay controls (optional) */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 border border-white/30">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Video description */}
                <div className="text-center mt-6">
                  <p className="text-lg text-gray-200 font-semibold mb-2">See BrandScapes AI In Action</p>
                  <p className="text-sm text-gray-400">Watch how easy it is to create professional mockups in seconds</p>
                </div>
              </div>
            </div>

            {/* CTA Elements - No Background Box */}
            <CTASection 
              className=""
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
            />

                  </div>
        </div>
      </div>

      {/* TRUST BAR - Social Proof */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 py-10 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            {/* Star Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-lg">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 text-yellow-500 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <div>
                <span className="text-2xl font-bold text-gray-900">4.9/5</span>
                <span className="text-gray-600 ml-1">Star Rating</span>
              </div>
            </div>
            
            {/* Active Users */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
              <div>
                <span className="text-2xl font-bold text-gray-900">10,000+</span>
                <span className="text-gray-600 ml-1">Active Users</span>
              </div>
            </div>
            
            {/* Mockups Generated */}
            <div className="flex items-center gap-4 group relative">
              {/* Enhanced Icon Container */}
              <div className="relative">
                {/* Multiple shadow layers for depth */}
                <div className="absolute -inset-1 bg-gradient-to-br from-blue-400/30 to-indigo-500/30 rounded-2xl blur-sm group-hover:blur-md transition-all duration-300"></div>
                <div className="absolute -inset-0.5 bg-gradient-to-br from-blue-500/40 to-indigo-600/40 rounded-2xl blur-xs group-hover:blur-sm transition-all duration-300"></div>
                
                {/* Main icon container with premium styling */}
                <div className="relative w-16 h-16 bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-2xl flex items-center justify-center border border-blue-100/50 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 ease-out">
                  {/* Inner glow */}
                  <div className="absolute inset-1 bg-gradient-to-br from-blue-400/10 to-indigo-500/10 rounded-xl"></div>
                  
                  {/* Enhanced icon with subtle animation */}
                  <div className="relative">
                    <svg className="w-8 h-8 text-blue-600 group-hover:text-indigo-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" className="opacity-20"/>
                      <path d="M4 4h16a2 2 0 0 1 2 2v8l-4-4-4 4-4-4-6 6V6a2 2 0 0 1 2-2z" className="opacity-60"/>
                      <circle cx="8.5" cy="8.5" r="1.5" className="opacity-80"/>
                      <path d="M14 10l4-4v8l-4-4z" className="opacity-40"/>
                </svg>
                    
                    {/* Floating mini icons for visual interest */}
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-gradient-to-br from-orange-400 to-yellow-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 animate-ping"></div>
              </div>
              </div>
                
                {/* Subtle rotating ring */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-r from-blue-400/20 via-transparent to-indigo-500/20 animate-spin-slow opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
              
              {/* Enhanced Text Content */}
              <div className="relative">
                {/* Animated background highlight */}
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-400/5 via-indigo-400/5 to-purple-400/5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                
                <div className="relative">
                  {/* Main number with enhanced styling */}
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:via-indigo-700 group-hover:to-purple-700 transition-all duration-300">
                      1,000,000
                    </span>
                    <span className="text-2xl font-bold text-blue-500 group-hover:text-indigo-600 transition-colors duration-300 animate-bounce">
                      +
                    </span>
          </div>
                  
                  {/* Enhanced label with icon */}
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-gray-700 font-medium group-hover:text-gray-800 transition-colors duration-300">
                      Mockups Generated
                    </span>
                    
                    {/* Success indicator */}
                    <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 animate-pulse"></div>
                  </div>
                  
                  {/* Subtle progress indicator */}
                  <div className="mt-2 w-24 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-600 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 ease-out"></div>
                  </div>
        </div>
      </div>

              {/* Floating particles effect */}
              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-blue-400 rounded-full animate-float-up"
                    style={{
                      left: `${20 + i * 15}%`,
                      top: `${20 + (i % 2) * 40}%`,
                      animationDelay: `${i * 200}ms`,
                      animationDuration: '2s'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHAT YOU'LL CREATE SECTION - Premium Light Theme */}
      <div className="relative py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 overflow-hidden">
        {/* Elegant background elements */}
        <div className="absolute inset-0">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(99, 102, 241, 0.4) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
          
          {/* Floating orbs for depth */}
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-200/20 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-purple-200/20 rounded-full filter blur-3xl animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-indigo-200/15 rounded-full filter blur-3xl animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Premium section header */}
          <div className="text-center mb-16">
            {/* Animated badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/70 backdrop-blur-sm border border-blue-200/50 rounded-full shadow-lg mb-8 animate-fade-in-down">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-3"></div>
              <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Creative Showcase</span>
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse ml-3 animation-delay-500"></div>
            </div>
            
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight animate-fade-in-up">
              <span className="block">What You'll Create in the</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 animate-gradient-x">
                Next 30 Seconds
              </span>
          </h3>
            
            <p className="text-xl text-gray-600 mb-4 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Transform your designs into stunning, professional mockups instantly. 
              <span className="font-semibold text-gray-800"> Here's a glimpse of what's possible.</span>
            </p>
            
            {/* Stats bar */}
            <div className="flex items-center justify-center gap-8 mt-8 animate-fade-in-up animation-delay-400">
              <div className="flex items-center gap-2 text-gray-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">200+ Templates</span>
              </div>
              <div className="w-1 h-4 bg-gray-300 rounded-full"></div>
              <div className="flex items-center gap-2 text-gray-600">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse animation-delay-500"></div>
                <span className="text-sm font-medium">4K Quality</span>
              </div>
              <div className="w-1 h-4 bg-gray-300 rounded-full"></div>
              <div className="flex items-center gap-2 text-gray-600">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse animation-delay-1000"></div>
                <span className="text-sm font-medium">Instant Results</span>
              </div>
            </div>
          </div>
          
          {/* Enhanced mockup grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Laptop Mockup - Enhanced */}
            <div className="group relative animate-fade-in-up">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                {/* Image container with enhanced styling */}
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                <img 
                  src="/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png"
                    alt="Laptop mockup showcase"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ease-out"
                  />
                  
                  {/* Enhanced floating badge with better animation */}
                  <div className="absolute top-4 right-4 transform translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-out">
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                      <div className="relative bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border border-white/20 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-200"></div>
                          <span className="uppercase tracking-wider">Most Popular</span>
                          <svg className="w-3 h-3 transform group-hover:scale-110 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
              </div>
              </div>
              </div>
            </div>
            
                  {/* Overlay icon */}
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                
                {/* Enhanced content */}
                <div className="p-6 relative">
                  <div className="absolute -top-3 left-6 w-6 h-6 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:animate-ping"></div>
                  
                  <h4 className="font-bold text-gray-900 text-xl mb-3 group-hover:text-blue-700 transition-colors duration-300">
                    Digital Displays
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Showcase your software on stunning laptop & desktop screens with photorealistic quality
                  </p>
                  
                  {/* Feature tags */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">Web Apps</span>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">Software</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* T-shirt Mockup - Enhanced */}
            <div className="group relative animate-fade-in-up animation-delay-100">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-purple-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                <img 
                  src="/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png"
                    alt="T-shirt mockup showcase"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ease-out"
                  />
                  
                  <div className="absolute top-4 right-4 transform translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-out">
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                      <div className="relative bg-gradient-to-r from-blue-500 to-cyan-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border border-white/20 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-200"></div>
                          <span className="uppercase tracking-wider">Trending Now</span>
                          <svg className="w-3 h-3 transform group-hover:scale-110 group-hover:animate-bounce transition-all duration-200" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                          </svg>
              </div>
              </div>
              </div>
            </div>
            
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                    </svg>
                  </div>
                </div>
                
                <div className="p-6 relative">
                  <div className="absolute -top-3 left-6 w-6 h-6 bg-purple-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:animate-ping"></div>
                  
                  <h4 className="font-bold text-gray-900 text-xl mb-3 group-hover:text-purple-700 transition-colors duration-300">
                    Apparel Design
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Perfect your logo placement on t-shirts, hoodies, and premium apparel
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">T-Shirts</span>
                    <span className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded-full font-medium">Hoodies</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Menu/Book Mockup - Enhanced */}
            <div className="group relative animate-fade-in-up animation-delay-200">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-green-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                <img 
                  src="/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png"
                    alt="Menu mockup showcase"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ease-out"
                  />
                  
                  <div className="absolute top-4 right-4 transform translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-out">
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                      <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border border-white/20 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-200"></div>
                          <span className="uppercase tracking-wider">Just Added</span>
                          <svg className="w-3 h-3 transform group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
              </div>
              </div>
              </div>
            </div>
            
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                
                <div className="p-6 relative">
                  <div className="absolute -top-3 left-6 w-6 h-6 bg-green-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:animate-ping"></div>
                  
                  <h4 className="font-bold text-gray-900 text-xl mb-3 group-hover:text-green-700 transition-colors duration-300">
                    Print Materials
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Bring your designs to life on books, menus, and premium print materials
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Menus</span>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">Books</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Product Box Mockup - Enhanced */}
            <div className="group relative animate-fade-in-up animation-delay-300">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-orange-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                <img 
                  src="/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png"
                    alt="Product box mockup showcase"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ease-out"
                  />
                  
                  <div className="absolute top-4 right-4 transform translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-out">
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-red-600 rounded-full blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                      <div className="relative bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border border-white/20 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-200"></div>
                          <span className="uppercase tracking-wider">🔥 Hot Deal</span>
                          <svg className="w-3 h-3 transform group-hover:scale-125 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                          </svg>
              </div>
              </div>
              </div>
            </div>
            
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 5v0m6 0v0" />
                    </svg>
                  </div>
                </div>
                
                <div className="p-6 relative">
                  <div className="absolute -top-3 left-6 w-6 h-6 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:animate-ping"></div>
                  
                  <h4 className="font-bold text-gray-900 text-xl mb-3 group-hover:text-orange-700 transition-colors duration-300">
                    Product Packaging
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Showcase your products in professional branded boxes and packaging
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">Boxes</span>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">Packages</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Coffee Mug Mockup - Enhanced */}
            <div className="group relative animate-fade-in-up animation-delay-400">
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-400 to-rose-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-pink-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                <img 
                  src="/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png"
                    alt="Coffee mug mockup showcase"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ease-out"
                  />
                  
                  <div className="absolute top-4 right-4 transform translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-out">
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                      <div className="relative bg-gradient-to-r from-pink-500 to-rose-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border border-white/20 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-200"></div>
                          <span className="uppercase tracking-wider">⭐ Best Seller</span>
                          <svg className="w-3 h-3 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-200" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
              </div>
              </div>
              </div>
            </div>
            
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                    <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                    </svg>
                  </div>
                </div>
                
                <div className="p-6 relative">
                  <div className="absolute -top-3 left-6 w-6 h-6 bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:animate-ping"></div>
                  
                  <h4 className="font-bold text-gray-900 text-xl mb-3 group-hover:text-pink-700 transition-colors duration-300">
                    Promotional Items
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Perfect your branding on mugs, merchandise, and promotional products
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded-full font-medium">Mugs</span>
                    <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full font-medium">Merch</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Billboard Mockup - Enhanced */}
            <div className="group relative animate-fade-in-up animation-delay-500">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-indigo-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                <img 
                  src="/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png"
                    alt="Billboard mockup showcase"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ease-out"
                  />
                  
                  <div className="absolute top-4 right-4 transform translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-out">
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                      <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border border-white/20 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-200"></div>
                          <span className="uppercase tracking-wider">💎 Premium</span>
                          <svg className="w-3 h-3 transform group-hover:rotate-45 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z" clipRule="evenodd" />
                          </svg>
              </div>
              </div>
              </div>
            </div>
                  
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
            </div>
          </div>
          
                <div className="p-6 relative">
                  <div className="absolute -top-3 left-6 w-6 h-6 bg-indigo-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:animate-ping"></div>
                  
                  <h4 className="font-bold text-gray-900 text-xl mb-3 group-hover:text-indigo-700 transition-colors duration-300">
                    Outdoor Advertising
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Scale your brand to massive billboards and outdoor advertising displays
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">Billboards</span>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">Displays</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Enhanced CTA Below Grid */}
          <div className="text-center animate-fade-in-up animation-delay-1600">
            {/* Premium container */}
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 rounded-3xl opacity-20 blur-xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-10 border border-white/50 shadow-xl">
                <p className="text-2xl text-gray-700 mb-6 leading-relaxed">
                  And this is just the beginning. Access 
                  <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> 200+ premium mockup templates </span>
                  instantly.
                </p>
                
                {/* Feature highlights */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="flex items-center justify-center gap-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl">
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="font-semibold text-gray-800">No Design Skills Needed</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full animate-pulse animation-delay-500"></div>
                    <span className="font-semibold text-gray-800">30-Second Generation</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl">
                    <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse animation-delay-1000"></div>
                    <span className="font-semibold text-gray-800">4K Quality Output</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HERO MOCKUP GALLERY - Auto-Scrolling Carousel */}
      <div className="relative bg-black py-20 overflow-hidden">
        {/* Background gradient for smooth transitions */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black"></div>
        
        <div className="relative text-center mb-12 px-4">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            From Flat Design to <span className="gradient-text-spectrum">Photorealistic Masterpiece</span> in Seconds
          </h3>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            This is the quality BrandScapes AI generates for you in just a few clicks. What will you create?
          </p>
        </div>
        
        {/* Auto-Scrolling Gallery Container */}
        <div className="relative">
          {/* Row 1 - Scrolling Left */}
          <div className="relative mb-8">
            <div className="flex animate-scroll-left will-transform">
              <div className="flex gap-4 px-2">
                {[
                  { src: "/sports-car.png", alt: "Sports car branding", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/spray bottle.png", alt: "Spray bottle product", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" },
                  { src: "/store-front.png", alt: "Store front branding", fallback: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png" },
                  { src: "/polo-tshirt.png", alt: "Polo shirt mockup", fallback: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png" },
                  { src: "/pillow-design.png", alt: "Pillow design", fallback: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png" },
                  { src: "/folded tshirts.png", alt: "Folded t-shirts", fallback: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png" },
                  { src: "/flag design.png", alt: "Flag design", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/bus-stop-sign.png", alt: "Bus stop advertising", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" },
                  { src: "/bus-wrapped.png", alt: "Bus wrap design", fallback: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png" },
                  { src: "/cap.png", alt: "Cap mockup", fallback: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png" },
                  { src: "/flag.png", alt: "Flag banner", fallback: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png" },
                  { src: "/bag-design.png", alt: "Bag design mockup", fallback: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png" },
                  { src: "/towel.png", alt: "Towel mockup", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/mockup-bf374287-4ace-4f6d-89c3-928d2c1ef3d0-laptop-screen.png", alt: "Laptop display", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" }
                ].map((item, i) => (
                  <div
                    key={`left-${i}`}
                    className="relative w-56 h-56 flex-shrink-0 rounded-xl overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer bg-gradient-to-br from-gray-800 to-gray-900"
                  >
                    <img 
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (item.fallback && target.src !== item.fallback) {
                          target.src = item.fallback;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              {/* Duplicate for infinite scroll */}
              <div className="flex gap-4 px-2">
                {[
                  { src: "/sports-car.png", alt: "Sports car branding", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/spray bottle.png", alt: "Spray bottle product", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" },
                  { src: "/store-front.png", alt: "Store front branding", fallback: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png" },
                  { src: "/polo-tshirt.png", alt: "Polo shirt mockup", fallback: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png" },
                  { src: "/pillow-design.png", alt: "Pillow design", fallback: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png" },
                  { src: "/folded tshirts.png", alt: "Folded t-shirts", fallback: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png" },
                  { src: "/flag design.png", alt: "Flag design", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/bus-stop-sign.png", alt: "Bus stop advertising", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" },
                  { src: "/bus-wrapped.png", alt: "Bus wrap design", fallback: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png" },
                  { src: "/cap.png", alt: "Cap mockup", fallback: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png" },
                  { src: "/flag.png", alt: "Flag banner", fallback: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png" },
                  { src: "/bag-design.png", alt: "Bag design mockup", fallback: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png" },
                  { src: "/towel.png", alt: "Towel mockup", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/mockup-bf374287-4ace-4f6d-89c3-928d2c1ef3d0-laptop-screen.png", alt: "Laptop display", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" }
                ].map((item, i) => (
                  <div
                    key={`left-dup-${i}`}
                    className="relative w-56 h-56 flex-shrink-0 rounded-xl overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer bg-gradient-to-br from-gray-800 to-gray-900"
                  >
                    <img 
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (item.fallback && target.src !== item.fallback) {
                          target.src = item.fallback;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Enhanced gradient overlays for Row 1 */}
            <div className="absolute inset-y-0 left-0 w-1/3 pointer-events-none z-10">
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 via-black/60 to-transparent"></div>
            </div>
            <div className="absolute inset-y-0 right-0 w-1/3 pointer-events-none z-10">
              <div className="absolute inset-0 bg-gradient-to-l from-black via-black/90 via-black/60 to-transparent"></div>
            </div>
          </div>
          
          {/* Row 2 - Scrolling Right */}
          <div className="relative">
            <div className="flex animate-scroll-right will-transform">
              <div className="flex gap-4 px-2">
                {[
                  { src: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png", alt: "Laptop mockup", fallback: "/sports-car.png" },
                  { src: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png", alt: "T-shirt front", fallback: "/polo-tshirt.png" },
                  { src: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png", alt: "Menu design", fallback: "/store-front.png" },
                  { src: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png", alt: "Product box", fallback: "/spray bottle.png" },
                  { src: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png", alt: "Coffee mug", fallback: "/pillow-design.png" },
                  { src: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png", alt: "Billboard", fallback: "/flag.png" },
                  { src: "/towel.png", alt: "Towel design", fallback: "/mockup-bf374287-4ace-4f6d-89c3-928d2c1ef3d0-laptop-screen.png" },
                  { src: "/bag-design.png", alt: "Shopping bag", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/sports-car.png", alt: "Vehicle wrap", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" },
                  { src: "/polo-tshirt.png", alt: "Apparel design", fallback: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png" },
                  { src: "/pillow-design.png", alt: "Home decor", fallback: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png" },
                  { src: "/cap.png", alt: "Cap design", fallback: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png" },
                  { src: "/bus-wrapped.png", alt: "Bus advertising", fallback: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png" },
                  { src: "/flag.png", alt: "Flag display", fallback: "/mockup-bf374287-4ace-4f6d-89c3-928d2c1ef3d0-laptop-screen.png" }
                ].map((item, i) => (
                  <div
                    key={`right-${i}`}
                    className="relative w-56 h-56 flex-shrink-0 rounded-xl overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer bg-gradient-to-br from-gray-800 to-gray-900"
                  >
                    <img 
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (item.fallback && target.src !== item.fallback) {
                          target.src = item.fallback;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              {/* Duplicate for infinite scroll */}
              <div className="flex gap-4 px-2">
                {[
                  { src: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png", alt: "Laptop mockup", fallback: "/sports-car.png" },
                  { src: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png", alt: "T-shirt front", fallback: "/polo-tshirt.png" },
                  { src: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png", alt: "Menu design", fallback: "/store-front.png" },
                  { src: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png", alt: "Product box", fallback: "/spray bottle.png" },
                  { src: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png", alt: "Coffee mug", fallback: "/pillow-design.png" },
                  { src: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png", alt: "Billboard", fallback: "/flag.png" },
                  { src: "/towel.png", alt: "Towel design", fallback: "/mockup-bf374287-4ace-4f6d-89c3-928d2c1ef3d0-laptop-screen.png" },
                  { src: "/bag-design.png", alt: "Shopping bag", fallback: "/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png" },
                  { src: "/sports-car.png", alt: "Vehicle wrap", fallback: "/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png" },
                  { src: "/polo-tshirt.png", alt: "Apparel design", fallback: "/mockup-038589c1-83bf-42da-9c39-2f50d2e0b20a-menu-restaurant.png" },
                  { src: "/pillow-design.png", alt: "Home decor", fallback: "/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png" },
                  { src: "/cap.png", alt: "Cap design", fallback: "/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png" },
                  { src: "/bus-wrapped.png", alt: "Bus advertising", fallback: "/mockup-6fc26ec9-3432-4e35-abbe-f40b779a3808-billboard-highway.png" },
                  { src: "/flag.png", alt: "Flag display", fallback: "/mockup-bf374287-4ace-4f6d-89c3-928d2c1ef3d0-laptop-screen.png" }
                ].map((item, i) => (
                  <div
                    key={`right-dup-${i}`}
                    className="relative w-56 h-56 flex-shrink-0 rounded-xl overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer bg-gradient-to-br from-gray-800 to-gray-900"
                  >
                    <img 
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (item.fallback && target.src !== item.fallback) {
                          target.src = item.fallback;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Enhanced gradient overlays for Row 2 */}
            <div className="absolute inset-y-0 left-0 w-1/3 pointer-events-none z-10">
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 via-black/60 to-transparent"></div>
            </div>
            <div className="absolute inset-y-0 right-0 w-1/3 pointer-events-none z-10">
              <div className="absolute inset-0 bg-gradient-to-l from-black via-black/90 via-black/60 to-transparent"></div>
            </div>
          </div>
        </div>
        
        {/* View More CTA */}
        
      </div>

      {/* BEFORE & AFTER TRANSFORMATION - Premium Light Design */}
      <div className="relative py-32 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 overflow-hidden">
        {/* Elegant background elements */}
        <div className="absolute inset-0">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(99, 102, 241, 0.3) 1px, transparent 1px)`,
              backgroundSize: '80px 80px'
            }}></div>
        </div>
        
          {/* Soft floating orbs */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-100/30 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-100/30 rounded-full filter blur-3xl animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-100/20 rounded-full filter blur-3xl animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Premium section header */}
          <div className="text-center mb-20">
            {/* Animated badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/70 backdrop-blur-sm border border-blue-200/50 rounded-full shadow-lg mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Visual Transformation</span>
            </div>
            
            <h3 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight animate-fade-in-up">
              <span className="block">The Visual</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 animate-gradient-x">
                "Before & After"
              </span>
              <span className="block">Transformation</span>
            </h3>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-5xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              This simple visual says it all. We took a basic logo file and, in less time than it takes to check your email, 
              <span className="font-semibold text-gray-800"> the AI turned it into multiple unique, professional, and ready-to-use marketing assets.</span>
            </p>
          </div>
          
          {/* Main transformation showcase */}
          <div className="relative max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-8 items-center">
              
              {/* BEFORE Section - Enhanced with Logo */}
              <div className="lg:col-span-2 text-center animate-fade-in-left">
          <div className="relative">
                  {/* Section Label */}
                  <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-red-100 to-orange-100 border border-red-200 rounded-full mb-8">
                    <svg className="w-4 h-4 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-bold text-red-700 uppercase tracking-wider">Before</span>
                  </div>
                  
                  {/* Before Container */}
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-orange-400 rounded-3xl opacity-20 blur-xl group-hover:opacity-40 transition-all duration-300"></div>
                    <div className="relative bg-white rounded-3xl p-12 shadow-xl border border-gray-200 transform group-hover:scale-105 transition-all duration-300">
                      
                      {/* Logo container - Fixed aspect ratio */}
                      <div className="relative mb-8">
                        <div className="w-48 h-32 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300 group-hover:border-gray-400 transition-colors duration-300 p-4">
                          <img 
                            src="/clawbrand_logo_color.png" 
                            alt="Basic logo file" 
                            className="max-w-full max-h-full object-contain opacity-60 group-hover:opacity-80 transition-opacity duration-300 filter grayscale group-hover:grayscale-0"
                          />
                        </div>
                        
                        {/* File info */}
                        <div className="mt-6 space-y-2">
                          <div className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full">
                            <svg className="w-4 h-4 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                            <span className="text-sm font-medium text-gray-600">clawbrand_logo_color.png</span>
                      </div>
                          <p className="text-gray-500 text-sm">Basic flat design file</p>
                    </div>
                  </div>
                      
                      {/* Limitations */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-center gap-2 text-red-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium">No context or environment</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-red-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium">Hard to visualize impact</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-red-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium">Low professional appeal</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* ARROW Section - Enhanced */}
              <div className="lg:col-span-1 text-center animate-fade-in animation-delay-200">
                <div className="relative py-8">
                  {/* Animated particles around arrow */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 relative">
                      <div className="absolute inset-0 animate-spin-slow">
                        <div className="absolute top-0 left-1/2 w-2 h-2 bg-orange-400 rounded-full -translate-x-1/2 animate-pulse"></div>
                        <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-purple-400 rounded-full -translate-x-1/2 animate-pulse animation-delay-500"></div>
                        <div className="absolute left-0 top-1/2 w-2 h-2 bg-blue-400 rounded-full -translate-y-1/2 animate-pulse animation-delay-1000"></div>
                        <div className="absolute right-0 top-1/2 w-2 h-2 bg-pink-400 rounded-full -translate-y-1/2 animate-pulse animation-delay-1500"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Main Arrow */}
                  <div className="relative z-10 mb-6">
                    <svg className="w-20 h-20 mx-auto transform hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24" fill="none">
                      <defs>
                        <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#f97316" />
                          <stop offset="50%" stopColor="#a855f7" />
                          <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                      <path d="M5 12h14m0 0l-7-7m7 7l-7 7" stroke="url(#arrowGradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  
                  {/* AI Badge */}
                  <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl blur opacity-50 animate-pulse"></div>
                    <div className="relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl text-white font-bold shadow-xl">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                      <span className="text-sm uppercase tracking-wider">AI Magic</span>
                    </div>
                  </div>
                  
                  {/* Timer */}
                  <div className="mt-4 inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-full shadow-lg">
                    <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <span className="text-blue-700 font-bold text-sm">30 Seconds</span>
                  </div>
                </div>
              </div>
              
              {/* AFTER Section - Dynamic Card Switching */}
              <div className="lg:col-span-2 text-center animate-fade-in-right animation-delay-400">
                <div className="relative">
                  {/* Section Label */}
                  <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200 rounded-full mb-8">
                    <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                    <span className="text-sm font-bold text-green-700 uppercase tracking-wider">After AI Processing</span>
                      </div>
                  
                  {/* Card Switching Animation Container */}
                  <div className="relative">
                    <div className="absolute -inset-2 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500 rounded-3xl opacity-20 blur-2xl animate-pulse"></div>
                    
                    {/* Main showcase area */}
                    <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-200 overflow-hidden">
                      {/* Background pattern */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50"></div>
                      
                      {/* Switching Cards Container - Enhanced */}
                      <div className="relative h-96 flex items-center justify-center">
                        
                        {/* Dynamic Card Stack - Cycling through mockups */}
                        <div className="relative w-80 h-80">
                          {/* Card 1 - Cap */}
                          <div className="absolute inset-0 transform rotate-0 z-30 transition-all duration-1000 ease-in-out" 
                               style={{
                                 animation: 'cardCycle1 12s infinite',
                                 animationDelay: '0s'
                               }}>
                            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 shadow-2xl border border-blue-200 h-full">
                              <img 
                                src="/cap.png"
                                alt="Cap mockup result"
                                className="w-full h-48 object-cover rounded-xl"
                              />
                              <div className="mt-4 text-center">
                                <div className="text-lg font-bold text-blue-700">Baseball Cap</div>
                                <div className="text-sm text-blue-600">Promotional merchandise</div>
                    </div>
                            </div>
                          </div>
                          
                          {/* Card 2 - T-shirt */}
                          <div className="absolute inset-0 transform rotate-3 translate-x-2 translate-y-1 z-20 transition-all duration-1000 ease-in-out opacity-70" 
                               style={{
                                 animation: 'cardCycle2 12s infinite',
                                 animationDelay: '3s'
                               }}>
                            <div className="relative bg-gradient-to-br from-purple-50 to-pink-100 rounded-2xl p-6 shadow-xl border border-purple-200 h-full">
                              <img 
                                src="/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png"
                                alt="T-shirt mockup result"
                                className="w-full h-48 object-cover rounded-xl"
                              />
                              <div className="mt-4 text-center">
                                <div className="text-lg font-bold text-purple-700">T-Shirt Design</div>
                                <div className="text-sm text-purple-600">Apparel collection</div>
                </div>
              </div>
            </div>
            
                          {/* Card 3 - Mug */}
                          <div className="absolute inset-0 transform -rotate-3 -translate-x-2 translate-y-2 z-10 transition-all duration-1000 ease-in-out opacity-50" 
                               style={{
                                 animation: 'cardCycle3 12s infinite',
                                 animationDelay: '6s'
                               }}>
                            <div className="relative bg-gradient-to-br from-orange-50 to-red-100 rounded-2xl p-6 shadow-lg border border-orange-200 h-full">
                              <img 
                                src="/mockup-5bd8f97f-8dba-46cc-ab43-e49f7f67ca91-mug.png"
                                alt="Mug mockup result"
                                className="w-full h-48 object-cover rounded-xl"
                              />
                              <div className="mt-4 text-center">
                                <div className="text-lg font-bold text-orange-700">Coffee Mug</div>
                                <div className="text-sm text-orange-600">Branded merchandise</div>
            </div>
                            </div>
                          </div>
                          
                          {/* Card 4 - Pillow */}
                          <div className="absolute inset-0 transform rotate-2 translate-x-3 -translate-y-1 z-5 transition-all duration-1000 ease-in-out opacity-30" 
                               style={{
                                 animation: 'cardCycle4 12s infinite',
                                 animationDelay: '9s'
                               }}>
                            <div className="relative bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 shadow-lg border border-green-200 h-full">
                              <img 
                                src="/pillow-design.png"
                                alt="Pillow mockup result"
                                className="w-full h-48 object-cover rounded-xl"
                              />
                              <div className="mt-4 text-center">
                                <div className="text-lg font-bold text-green-700">Throw Pillow</div>
                                <div className="text-sm text-green-600">Home decor</div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Plus indicator for more */}
                          <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg z-40 animate-bounce">
                            <span className="text-xl">+</span>
                          </div>
                          
                          {/* Active indicator dots */}
                          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{animation: 'indicatorCycle1 12s infinite'}}></div>
                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{animation: 'indicatorCycle2 12s infinite', animationDelay: '3s'}}></div>
                            <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" style={{animation: 'indicatorCycle3 12s infinite', animationDelay: '6s'}}></div>
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{animation: 'indicatorCycle4 12s infinite', animationDelay: '9s'}}></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Results Summary */}
                      <div className="mt-6 grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">3+</div>
                          <div className="text-xs text-gray-600">Unique Mockups</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">4K</div>
                          <div className="text-xs text-gray-600">Quality</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">30s</div>
                          <div className="text-xs text-gray-600">Generation</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Process Steps */}
            <div className="mt-20 grid md:grid-cols-4 gap-6 animate-fade-in-up animation-delay-600">
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  1
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Upload Logo</h4>
                <p className="text-sm text-gray-600">Drag & drop any design file</p>
              </div>
              
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  2
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Choose Template</h4>
                <p className="text-sm text-gray-600">Pick from 200+ options</p>
              </div>
              
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  3
                </div>
                <h4 className="font-bold text-gray-900 mb-2">AI Processing</h4>
                <p className="text-sm text-gray-600">Magic happens automatically</p>
              </div>
              
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  4
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Download 4K</h4>
                <p className="text-sm text-gray-600">Perfect quality results</p>
              </div>
            </div>
          </div>
          
          {/* CTA Button */}
          <div className="text-center mt-20 animate-fade-in-up animation-delay-800">
            <div className="relative inline-block">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
              <button className="relative group inline-flex items-center px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                <svg className="w-6 h-6 mr-3 group-hover:animate-spin" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                See The Magic In Action
                <svg className="w-5 h-5 ml-3 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* THE VISUALS GAP PROBLEM SECTION - Premium Purple-Blue Design */}
      <div className="relative py-32 overflow-hidden">
        {/* Premium gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-700 to-blue-800"></div>
        
        {/* Elegant overlay gradients for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        
        {/* Sophisticated background elements */}
        <div className="absolute inset-0">
          {/* Subtle mesh pattern */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
              backgroundSize: '80px 80px'
            }}></div>
          </div>
          
          {/* Floating orbs for ambiance */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-400/20 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-400/20 rounded-full filter blur-3xl animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-400/15 rounded-full filter blur-3xl animate-float animation-delay-4000"></div>
        </div>
        
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Premium section header */}
          <div className="text-center mb-20">
            {/* Warning badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full shadow-lg mb-8 animate-fade-in-down">
              <svg className="w-5 h-5 text-orange-300 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-semibold text-orange-200 uppercase tracking-wider">Critical Business Problem</span>
            </div>
            
            {/* Main headline with enhanced styling */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-center mb-8 leading-tight animate-fade-in-up">
            <span className="block text-white">If Your Brand</span>
              <span className="block relative mt-2">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-red-400 to-pink-400 animate-gradient-x">
                <em>Looks</em> Cheap,
              </span>
                {/* Enhanced glow effects */}
                <span className="absolute inset-0 text-orange-400/50 blur-sm animate-pulse" aria-hidden="true">
                <em>Looks</em> Cheap,
              </span>
              </span>
              <span className="block text-white mt-2">Your Business <em className="text-orange-300">Is</em> Cheap.</span>
          </h2>
          
            {/* Enhanced subheadline - Dark Glass Design */}
            <div className="flex justify-center mb-16 animate-fade-in-up animation-delay-200">
              <div className="relative max-w-4xl">
                <div className="relative bg-black/60 backdrop-blur-xl border border-gray-800/60 rounded-3xl p-10 shadow-2xl">
                  {/* Icon on top */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-black/70 backdrop-blur-lg border border-gray-700/60 rounded-2xl flex items-center justify-center shadow-2xl">
                      <svg className="w-10 h-10 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
                  </div>
                  
                  {/* Text in one row */}
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center leading-tight">
                The painful "Visuals Gap" is killing trust and costing you sales
              </h3>
                </div>
              </div>
            </div>
            </div>
            
          {/* Enhanced stats showcase */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-fade-in-up animation-delay-400">
              {/* Stat 1 */}
              <div className="relative group">
                <div className="relative bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl p-8 text-center transform group-hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl">
                  <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-300 to-orange-300 mb-4">3</div>
                  <div className="text-white font-semibold text-lg mb-2">Seconds</div>
                  <div className="text-gray-200 text-sm">to judge your brand</div>
                  
                  {/* Icon */}
                  <div className="absolute -top-3 -right-3 w-10 h-10 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                </div>
                </div>
              </div>
              
              {/* Stat 2 */}
              <div className="relative group">
                <div className="relative bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl p-8 text-center transform group-hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl">
                  <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-300 to-yellow-300 mb-4">75%</div>
                  <div className="text-white font-semibold text-lg mb-2">Judge by</div>
                  <div className="text-gray-200 text-sm">visual appearance</div>
                  
                  <div className="absolute -top-3 -right-3 w-10 h-10 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Stat 3 */}
              <div className="relative group">
                <div className="relative bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl p-8 text-center transform group-hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl">
                  <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-red-300 mb-4">94%</div>
                  <div className="text-white font-semibold text-lg mb-2">Won't trust</div>
                  <div className="text-gray-200 text-sm">"cheap" looking sites</div>
                  
                  <div className="absolute -top-3 -right-3 w-10 h-10 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Enhanced story content */}
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              
              {/* Left side - Story content */}
              <div className="space-y-8 text-lg leading-relaxed animate-fade-in-left animation-delay-600">
                <div className="relative bg-white/12 backdrop-blur-md border border-white/25 rounded-2xl p-8 shadow-xl">
                  <p className="text-gray-100 mb-6">
                  You could have the best product in the world... the most persuasive copy... and the most targeted traffic... 
                    But if your brand <strong className="text-orange-300 bg-white/20 px-2 py-1 rounded backdrop-blur-sm">looks</strong> cheap, you're dead in the water.
                </p>
                
                  <p className="text-gray-100">
                  In today's market, perception is reality. Buyers make a snap judgment about your quality and trustworthiness 
                  in the first three seconds they land on your page. And that judgment is almost entirely 
                    <strong className="text-orange-300 bg-white/20 px-2 py-1 rounded backdrop-blur-sm">visual.</strong>
                  </p>
                </div>
                
                {/* Pain points with enhanced design */}
                <div className="space-y-6">
                  <div className="flex items-start group">
                    <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl flex items-center justify-center mr-4 mt-1 group-hover:scale-105 transition-transform duration-300">
                      <span className="text-red-300 text-lg">😰</span>
                    </div>
                    <div className="relative flex-1">
                      <div className="relative bg-white/10 backdrop-blur-sm border border-white/25 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
                        <p className="text-gray-100 leading-relaxed">
                          It's that flash of doubt when you publish a new sales page, wondering if people will trust you enough to click "buy."
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start group">
                    <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl flex items-center justify-center mr-4 mt-1 group-hover:scale-105 transition-transform duration-300">
                      <span className="text-orange-300 text-lg">💸</span>
                    </div>
                    <div className="relative flex-1">
                      <div className="relative bg-white/10 backdrop-blur-sm border border-white/25 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
                        <p className="text-gray-100 leading-relaxed">
                          It's the frustration of having a killer idea for a promotion, but knowing it would take a week and $500 
                          in designer fees to get the visuals you need... so the idea dies, and the opportunity is lost.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side - The Visuals Gap showcase */}
              <div className="animate-fade-in-right animation-delay-800">
                <div className="relative">
                  {/* Main gap container */}
                  <div className="relative">
                    <div className="relative bg-white/15 backdrop-blur-md border border-white/30 rounded-3xl p-10 text-center shadow-xl">
                      <div className="mb-6">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl mb-4 shadow-lg">
                          <svg className="w-10 h-10 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                      </div>
                      
                      <h4 className="text-4xl font-black text-center mb-6">
                        <span className="text-red-300">The</span>{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                          Visuals Gap
                        </span>
                      </h4>
                      
                      <p className="text-xl text-gray-100 text-center leading-relaxed mb-8">
                        It's the painful gap between the professional, high-value brand you see in your head... 
                        and the amateur-looking reality on your screen.
                      </p>
                      
                      {/* Visual representation */}
                      <div className="grid grid-cols-2 gap-6 mt-8">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                            <svg className="w-8 h-8 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                    </div>
                          <div className="text-sm font-semibold text-green-300">In Your Head</div>
                          <div className="text-xs text-gray-300">Professional & Valuable</div>
                </div>
                
                        <div className="text-center">
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                            <svg className="w-8 h-8 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                            </svg>
                    </div>
                          <div className="text-sm font-semibold text-red-300">On Your Screen</div>
                          <div className="text-xs text-gray-300">Amateur & Cheap</div>
                  </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
                
        </div>
      </div>

      </div>

      {/* THE OLD FRUSTRATING CYCLE - Premium Light Design */}
      <div className="relative py-32 bg-gradient-to-br from-gray-50 via-red-50 to-orange-50 overflow-hidden">
        {/* Elegant background elements */}
        <div className="absolute inset-0">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(239, 68, 68, 0.4) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
          
          {/* Soft floating orbs */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-red-100/30 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-100/30 rounded-full filter blur-3xl animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-yellow-100/20 rounded-full filter blur-3xl animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Premium section header */}
          <div className="text-center mb-20">
            {/* Animated badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/70 backdrop-blur-sm border border-red-200/50 rounded-full shadow-lg mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-semibold text-red-700 uppercase tracking-wider">The Frustrating Reality</span>
            </div>
            
          {/* Headline with frustration emphasis */}
            <h3 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight animate-fade-in-up">
              You're Stuck In A 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600 animate-gradient-x">
                Frustrating Cycle
              </span>
            </h3>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto animate-fade-in-up animation-delay-200">
              Every option leads to the same dead end: wasted time, wasted money, and mediocre results.
            </p>
          </div>
          
          {/* Three frustrating options with premium light design */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Option 1: Freelancer */}
            <div className="relative group animate-fade-in-up animation-delay-400">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-red-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-3xl border border-red-200 p-8 h-full transform group-hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl">
                {/* Icon with enhanced styling */}
                <div className="relative mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-red-100 to-red-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-5xl">👥</span>
                  </div>
                  {/* Price tag */}
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg">
                    $300+
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  <h4 className="text-3xl font-bold text-gray-900 mb-2">Option 1</h4>
                  <h5 className="text-xl font-semibold text-red-600 mb-6">Hire a Freelancer</h5>
                </div>
                
                {/* Pain points with enhanced design */}
                <ul className="space-y-4 text-gray-700 mb-8">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Endless back-and-forth revisions</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Wait days or weeks for delivery</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>$50-150 per mockup adds up fast</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Results are just "okay" at best</span>
                  </li>
                </ul>
                
                {/* Time indicator */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-4 border border-red-200">
                  <div className="flex items-center justify-center text-red-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <span className="font-bold">1-2 Weeks Wasted</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Option 2: DIY */}
            <div className="relative group animate-fade-in-up animation-delay-600">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-3xl border border-orange-200 p-8 h-full transform group-hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl">
                {/* Icon with enhanced styling */}
                <div className="relative mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-5xl">🎨</span>
                  </div>
                  {/* Time tag */}
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg">
                    8hrs+
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  <h4 className="text-3xl font-bold text-gray-900 mb-2">Option 2</h4>
                  <h5 className="text-xl font-semibold text-orange-600 mb-6">DIY in Photoshop</h5>
                </div>
                
                {/* Pain points with enhanced design */}
                <ul className="space-y-4 text-gray-700 mb-8">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Steep learning curve hell</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Hours of YouTube tutorials</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Amateur results look cheap</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>$20/month Adobe tax forever</span>
                  </li>
                </ul>
                
                {/* Frustration indicator */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-4 border border-orange-200">
                  <div className="flex items-center justify-center text-orange-700">
                    <span className="text-2xl mr-2">😤</span>
                    <span className="font-bold">Maximum Frustration</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Option 3: Basic Tools */}
            <div className="relative group animate-fade-in-up animation-delay-800">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
              <div className="relative bg-white rounded-3xl border border-yellow-200 p-8 h-full transform group-hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl">
                {/* Icon with enhanced styling */}
                <div className="relative mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-5xl">🔧</span>
                  </div>
                  {/* Generic tag */}
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg">
                    Generic
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  <h4 className="text-3xl font-bold text-gray-900 mb-2">Option 3</h4>
                  <h5 className="text-xl font-semibold text-yellow-600 mb-6">Clunky Template Tools</h5>
                </div>
                
                {/* Pain points with enhanced design */}
                <ul className="space-y-4 text-gray-700 mb-8">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Cookie-cutter templates</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Zero customization options</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Looks like everyone else's</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Still costs $29-49/month</span>
                  </li>
                </ul>
                
                {/* Warning indicator */}
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4 border border-yellow-200">
                  <div className="flex items-center justify-center text-yellow-700">
                    <span className="text-2xl mr-2">⚠️</span>
                    <span className="font-bold">Brand Suicide</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* The Real Enemy - Enhanced Light Design */}
          <div className="relative mb-16 animate-scale-in animation-delay-1000">
            {/* Main container with premium styling */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-5xl mx-auto border border-gray-200">
              {/* Decorative top accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500"></div>
              
              {/* Content container */}
              <div className="relative p-12 md:p-16 bg-gradient-to-br from-white via-gray-50 to-white">
                {/* Icon header */}
                <div className="flex justify-center mb-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-2xl">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                
                {/* Main text with professional styling */}
                <div className="text-center max-w-4xl mx-auto">
                  <p className="text-3xl md:text-4xl text-gray-800 font-light leading-relaxed mb-6">
                  The enemy isn't a lack of ideas. 
                  </p>
                  <p className="text-3xl md:text-4xl text-gray-800 font-light leading-relaxed mb-6">
                    It's the <span className="font-bold text-red-600 px-1">friction</span>.
                  </p>
                  <p className="text-3xl md:text-4xl text-gray-800 font-light leading-relaxed mb-8">
                    It's the <span className="font-bold text-orange-600 px-1">bottleneck</span>.
                  </p>
                  <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed">
                    It's the wall you hit every time you need a simple, 
                    professional image to make your business look <span className="font-bold text-gray-900 border-b-4 border-orange-300 px-1">legitimate</span>.
                </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Transition to solution */}
          <div className="text-center animate-fade-in-up animation-delay-1200">
            <div className="max-w-4xl mx-auto mb-12">
              <p className="text-3xl md:text-4xl text-gray-700 mb-6 font-light leading-relaxed">
              What if you could tear down that wall 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 font-bold"> forever?</span>
              </p>
              
              <div className="relative inline-block">
                <div className="absolute -inset-3 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl opacity-20 blur-2xl"></div>
                <h3 className="relative text-5xl md:text-6xl lg:text-7xl font-black text-gray-900">
                  That's <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">BrandScapes AI.</span>
              </h3>
              </div>
            </div>
            
            {/* BrandScapes AI Product Image */}
            <div className="relative max-w-6xl mx-auto animate-fade-in-up animation-delay-1400">
                <img 
                  src="/brandscapes-all.png" 
                  alt="BrandScapes AI - Complete Product Mockup Solution" 
                  className="w-full h-auto"
                />
            </div>

            <div className="inline-block relative group animate-fade-in-up animation-delay-1600 mt-20">
              <CTASection 
                className=""
                showHeadline={true}
                showRating={true}
                showPaymentImage={true}
                useDarkPaymentImage={true} 
                darkMode={true} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS SECTION - Professional Alternating Layout */}
      <div className="relative py-32 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 overflow-hidden">
        {/* Elegant background elements */}
        <div className="absolute inset-0">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.015]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(99, 102, 241, 0.4) 1px, transparent 1px)`,
              backgroundSize: '100px 100px'
            }}></div>
          </div>
          
          {/* Soft floating orbs */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-100/20 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-100/20 rounded-full filter blur-3xl animate-float animation-delay-2000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Premium section header */}
          <div className="text-center mb-24">
            {/* Animated badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/80 backdrop-blur-sm border border-blue-200 rounded-full shadow-lg mb-8 animate-fade-in-down">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-3"></div>
              <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">AI-Powered Transformation</span>
            </div>
            
            {/* Main headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8 animate-fade-in-up leading-tight">
              <span className="block text-gray-600 text-2xl md:text-3xl lg:text-4xl mb-4 font-normal">
                Transform Your Brand Assets Into
              </span>
              <span className="block text-gray-900">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 animate-gradient-x">
                  Professional Mockups
                </span>
              </span>
              <span className="block text-gray-900 mt-2">
                In Just <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500 font-black">30 Seconds</span>
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Watch our AI transform your simple design into a photorealistic masterpiece. 
              No design skills needed – just 
              <span className="text-gray-800 font-semibold bg-gradient-to-r from-blue-100 to-purple-100 px-3 py-1 rounded-lg mx-1">
                Choose → Upload → Customize → Download
              </span>
            </p>
          </div>
          
          {/* Process Steps - Alternating Layout with Floating Images */}
          <div className="space-y-32">
              
              {/* Step 1: Choose Template - Image Left, Text Right */}
              <div className="relative grid lg:grid-cols-2 gap-16 items-center">
                {/* Floating Image - Left */}
                <div className="relative group">
                      <img 
                        src="/Step1.png" 
                        alt="Choose Template Interface" 
                    className="w-full h-auto rounded-3xl shadow-2xl transform group-hover:scale-[1.03] transition-all duration-500 hover:shadow-3xl"
                      />
            </div>
            
                {/* Text Content - Right */}
                <div className="relative">
                  <div className="absolute -top-6 -left-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <span className="text-2xl font-black text-white">01</span>
                    </div>
                  </div>
                  
                  <div className="pt-12">
                    <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300 rounded-full mb-6">
                      <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                      </svg>
                      <span className="text-sm font-semibold text-purple-700 uppercase tracking-wider">Template Selection</span>
                  </div>
                  
                    <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                      Choose Your Perfect
                      <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                        Mockup Template
                      </span>
                    </h3>
                    
                    <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                      Browse through our extensive library of 200+ professionally designed mockup templates. 
                      From product boxes to billboards, find the perfect canvas for your design in seconds.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Instant preview of all templates</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Filter by category and style</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">One-click template selection</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Step 2: Upload Assets - Text Left, Image Right */}
              <div className="relative grid lg:grid-cols-2 gap-16 items-center">
                {/* Text Content - Left */}
                <div className="relative order-2 lg:order-1">
                  <div className="absolute -top-6 -right-6 lg:-left-6 lg:right-auto">
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <span className="text-2xl font-black text-white">02</span>
                    </div>
                  </div>
                  
                  <div className="pt-12">
                    <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-pink-100 to-orange-100 border border-pink-300 rounded-full mb-6">
                      <svg className="w-5 h-5 text-pink-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm font-semibold text-pink-700 uppercase tracking-wider">Asset Upload</span>
                    </div>
                    
                    <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                      Upload Your Brand
                      <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-orange-600">
                        Assets & Designs
                      </span>
                    </h3>
                    
                    <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                      Simply drag and drop your logo, design, or any image file. Our AI handles the rest, 
                      ensuring perfect placement and realistic rendering every time.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Supports PNG, JPG, SVG formats</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Automatic background removal</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Secondary image support (optional)</span>
                      </div>
                    </div>
                    </div>
                  </div>
                  
                {/* Floating Image - Right */}
                <div className="relative group order-1 lg:order-2">
                      <img 
                        src="/Step2.png" 
                        alt="Upload Assets Interface" 
                    className="w-full h-auto rounded-3xl shadow-2xl transform group-hover:scale-[1.03] transition-all duration-500 hover:shadow-3xl"
                      />
                </div>
              </div>
              
              {/* Step 3: Customize - Image Left, Text Right */}
              <div className="relative grid lg:grid-cols-2 gap-16 items-center">
                {/* Floating Image - Left */}
                <div className="relative group">
                      <img 
                        src="/Step3.png" 
                        alt="Customize Settings Interface" 
                    className="w-full h-auto rounded-3xl shadow-2xl transform group-hover:scale-[1.03] transition-all duration-500 hover:shadow-3xl"
                      />
                  </div>
                  
                {/* Text Content - Right */}
                    <div className="relative">
                  <div className="absolute -top-6 -left-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <span className="text-2xl font-black text-white">03</span>
                    </div>
                  </div>
                  
                  <div className="pt-12">
                    <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-300 rounded-full mb-6">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Customization</span>
                    </div>
                    
                    <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                      Perfect Every Detail
                      <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
                        With AI Precision
                      </span>
                    </h3>
                    
                    <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                      Fine-tune your mockup with powerful customization options. Adjust colors, positioning, 
                      shadows, and more to achieve the exact look you envision.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Background and foreground colors</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Shadow intensity and direction</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Text and tagline options</span>
                      </div>
                    </div>
                  </div>
                    </div>
                  </div>
                  
              {/* Step 4: Download - Text Left, Image Right */}
              <div className="relative grid lg:grid-cols-2 gap-16 items-center">
                {/* Text Content - Left */}
                <div className="relative order-2 lg:order-1">
                  <div className="absolute -top-6 -right-6 lg:-left-6 lg:right-auto">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <span className="text-2xl font-black text-white">04</span>
                </div>
              </div>
              
                  <div className="pt-12">
                    <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 rounded-full mb-6">
                      <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span className="text-sm font-semibold text-green-700 uppercase tracking-wider">Download & Export</span>
                    </div>
                    
                    <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                      Export in Stunning
                      <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
                        4K Resolution
                      </span>
                    </h3>
                    
                    <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                      Download your masterpiece in ultra-high resolution. Perfect for print, web, 
                      social media, or any platform where quality matters.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                        </div>
                        <span className="text-gray-700 font-medium">4K resolution (4096x4096px)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">PNG & JPG formats available</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">Transparent background option</span>
                    </div>
                  </div>
                  
                    <div className="mt-8 inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-full shadow-lg">
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Ready in 30 seconds!
                    </div>
                    </div>
                  </div>
                  
                {/* Floating Image - Right */}
                <div className="relative group order-1 lg:order-2">
                      <img 
                        src="/Step4.png" 
                        alt="Download Graphics Interface" 
                    className="w-full h-auto rounded-3xl shadow-2xl transform group-hover:scale-[1.03] transition-all duration-500 hover:shadow-3xl"
                      />
              </div>
            </div>
          </div>
          
          {/* Zero Learning Curve Badge - Light Glass-Morphism Design */}
          <div className="text-center mt-24 mb-20 animate-fade-in-up animation-delay-600">
            <div className="relative inline-block max-w-4xl">
              <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-3xl px-12 py-10 shadow-xl">
                {/* Content */}
                    <div className="relative">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                        </svg>
                    </div>
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Zero Learning Curve</h3>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                    We've eliminated complexity. If you can click a mouse, you can create 
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 font-bold"> world-class visuals </span>
                    that look like they cost thousands.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Features Row - Light Glass-Morphism Design */}
          <div className="relative mt-16 mb-20">
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              {[
                { text: '200+ Templates', icon: '🎨', color: 'from-blue-500 to-indigo-500' },
                { text: '4K Exports', icon: '📸', color: 'from-purple-500 to-pink-500' },
                { text: 'AI Background Removal', icon: '✨', color: 'from-orange-500 to-amber-500' },
                { text: '30-Second Generation', icon: '⚡', color: 'from-green-500 to-emerald-500' },
                { text: 'One-Time Payment', icon: '💰', color: 'from-red-500 to-orange-500' }
            ].map((feature, index) => (
              <div 
                key={feature.text} 
                  className="group relative animate-scale-in"
                style={{ animationDelay: `${800 + index * 100}ms` }}
                >
                {/* Glass-morphism card */}
                <div className="relative bg-white/70 backdrop-blur-md border border-gray-200 px-8 py-5 rounded-2xl shadow-lg transform group-hover:scale-105 group-hover:bg-white/80 transition-all duration-300">
                  <div className="flex items-center gap-4">
                  <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                        <span className="text-2xl">{feature.icon}</span>
                        </div>
                    </div>
                    <span className="text-gray-800 font-bold text-lg group-hover:text-gray-900 transition-colors duration-300">
                          {feature.text}
                        </span>
                    </div>
                </div>
              </div>
            ))}
          </div>
            
            {/* Enhanced Light CTA */}
            <div className="text-center mt-20">
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 rounded-3xl opacity-20 blur-2xl"></div>
                <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-3xl p-10 shadow-xl">
            <CTASection 
              className=""
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
                    useDarkPaymentImage={true} 
                    darkMode={true} 
            />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI DESIGN STUDIO FEATURES - Premium Redesign */}
      <div className="relative py-32 bg-black overflow-hidden">
        {/* Advanced background with depth */}
        <div className="absolute inset-0">
          {/* Gradient mesh background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/40 via-gray-950 to-purple-950/40"></div>
          
          {/* Animated mesh pattern */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234c1d95' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              animation: 'subtle-shift 20s ease-in-out infinite'
            }}></div>
          </div>
          
          {/* Floating light orbs */}
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-600 rounded-full mix-blend-screen filter blur-[150px] opacity-20 animate-float"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-purple-600 rounded-full mix-blend-screen filter blur-[150px] opacity-20 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Section header with enhanced design */}
          <div className="text-center mb-20">
            {/* Animated badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-full mb-8 animate-fade-in-down">
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse mr-3"></div>
              <span className="text-sm text-purple-300 font-medium uppercase tracking-wider">Complete Design Suite</span>
            </div>
            
            {/* Main headline with gradient */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 animate-fade-in-up">
              An Entire 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 animate-gradient-x">
                A.I. Design Studio
              </span>
              <span className="text-white">At Your Fingertips</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              From one-click background removal to total customization and 4K exports, 
              <span className="text-white font-semibold"> everything you need </span>
              to create premium visuals is right here.
            </p>
          </div>
          
          {/* Feature cards grid - 3D design */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Feature 1: AI Photorealism */}
            <div className="group relative animate-fade-in-up animation-delay-400" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5 group-hover:scale-105" style={{ transformStyle: 'preserve-3d' }}>
                {/* Card glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                {/* Card content */}
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 overflow-hidden h-full min-h-[420px] flex flex-col">
                  {/* Internal gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Icon container */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                        <path d="M9 12l-2 2 5 5 7-7-1.41-1.41L12 16.17 9.41 13.59z" fill="white" opacity="0.7"/>
                      </svg>
                    </div>
                    {/* Floating particles */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-purple-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping animation-delay-1000"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300">
                    AI-Perfected Photorealism
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                    Our AI doesn't just paste your image; it <span className="text-gray-200">rebuilds the entire scene</span>. Understanding lighting, perspective, and shadows to create mockups that rival professional photography.
                  </p>
                  
                  {/* Feature highlights */}
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {[
                      { icon: '💡', text: 'Smart Lighting' },
                      { icon: '🎭', text: 'Perfect Shadows' },
                      { icon: '🎯', text: '3D Perspective' }
                    ].map((tag) => (
                      <span key={tag.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-xs font-medium text-gray-300 group-hover:border-blue-500/50 group-hover:text-blue-400 group-hover:from-blue-900/20 group-hover:to-blue-800/20 transition-all duration-300">
                        <span className="text-sm">{tag.icon}</span>
                        {tag.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 2: Template Library */}
            <div className="group relative animate-fade-in-up animation-delay-600" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5 group-hover:scale-105" style={{ transformStyle: 'preserve-3d' }}>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 overflow-hidden h-full min-h-[420px] flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 to-pink-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z"/>
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-pink-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping animation-delay-1000"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-pink-400 transition-all duration-300">
                    200+ Premium Templates
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                    Never run out of creative options. Access a <span className="text-gray-200">massive library</span> spanning 11 categories from digital devices to apparel, all professionally designed.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {[
                      { icon: '📁', text: '11 Categories' },
                      { icon: '🔄', text: 'Weekly Updates' },
                      { icon: '✨', text: 'Exclusive Designs' }
                    ].map((tag) => (
                      <span key={tag.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-xs font-medium text-gray-300 group-hover:border-purple-500/50 group-hover:text-purple-400 group-hover:from-purple-900/20 group-hover:to-purple-800/20 transition-all duration-300">
                        <span className="text-sm">{tag.icon}</span>
                        {tag.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 3: Background Removal */}
            <div className="group relative animate-fade-in-up animation-delay-800" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5 group-hover:scale-105" style={{ transformStyle: 'preserve-3d' }}>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 overflow-hidden h-full min-h-[420px] flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-600/5 to-amber-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM5 19V5h14v14H5z"/>
                        <path d="M13.96 12.29l-2.75 3.54-1.96-2.36L6.5 17h11l-3.54-4.71z" opacity="0.7"/>
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-amber-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping animation-delay-1000"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-orange-400 group-hover:to-amber-400 transition-all duration-300">
                    AI Background Removal
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                    One-click magic powered by Bria AI. <span className="text-gray-200">Remove any background</span> with surgical precision in seconds. Perfect for e-commerce and professional presentations.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {[
                      { icon: '🖱️', text: 'One Click' },
                      { icon: '✂️', text: 'Hair-Perfect' },
                      { icon: '🔍', text: 'Transparent Export' }
                    ].map((tag) => (
                      <span key={tag.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-xs font-medium text-gray-300 group-hover:border-orange-500/50 group-hover:text-orange-400 group-hover:from-orange-900/20 group-hover:to-orange-800/20 transition-all duration-300">
                        <span className="text-sm">{tag.icon}</span>
                        {tag.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 4: Customization */}
            <div className="group relative animate-fade-in-up animation-delay-1000" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5 group-hover:scale-105" style={{ transformStyle: 'preserve-3d' }}>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 overflow-hidden h-full min-h-[420px] flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 to-emerald-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        <path d="M17.65 14.35l-.71-.71c.19-.39.3-.82.3-1.28 0-1.66-1.34-3-3-3s-3 1.34-3 3 1.34 3 3 3c.46 0 .89-.11 1.28-.3l.71.71L19 19l1-1-2.35-2.35z" fill="white" opacity="0.7"/>
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-emerald-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping animation-delay-1000"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-emerald-400 transition-all duration-300">
                    Total Customization
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                    Make it yours with <span className="text-gray-200">complete creative control</span>. Adjust colors, lighting, shadows, add effects, and fine-tune every detail to match your vision.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {[
                      { icon: '🎨', text: 'Color Control' },
                      { icon: '✨', text: 'Effects Library' },
                      { icon: '👁️', text: 'Real-time Preview' }
                    ].map((tag) => (
                      <span key={tag.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-xs font-medium text-gray-300 group-hover:border-green-500/50 group-hover:text-green-400 group-hover:from-green-900/20 group-hover:to-green-800/20 transition-all duration-300">
                        <span className="text-sm">{tag.icon}</span>
                        {tag.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 5: Export Quality */}
            <div className="group relative animate-fade-in-up animation-delay-1200" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5 group-hover:scale-105" style={{ transformStyle: 'preserve-3d' }}>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 overflow-hidden h-full min-h-[420px] flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-pink-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-pink-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/>
                        <text x="8" y="15" fill="white" fontSize="8" fontWeight="bold">4K</text>
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-pink-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping animation-delay-1000"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-red-400 group-hover:to-pink-400 transition-all duration-300">
                    4K & Print-Ready
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                    Export in <span className="text-gray-200">ultra-sharp 4K resolution</span> for digital use or 300 DPI for perfect prints. Your mockups will look stunning on any medium.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {[
                      { icon: '📺', text: '4K Resolution' },
                      { icon: '🖨️', text: '300 DPI Print' },
                      { icon: '📂', text: 'Multiple Formats' }
                    ].map((tag) => (
                      <span key={tag.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-xs font-medium text-gray-300 group-hover:border-red-500/50 group-hover:text-red-400 group-hover:from-red-900/20 group-hover:to-red-800/20 transition-all duration-300">
                        <span className="text-sm">{tag.icon}</span>
                        {tag.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 6: Team Collaboration */}
            <div className="group relative animate-fade-in-up animation-delay-1400" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5 group-hover:scale-105" style={{ transformStyle: 'preserve-3d' }}>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 overflow-hidden h-full min-h-[420px] flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-blue-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping animation-delay-1000"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-indigo-400 group-hover:to-blue-400 transition-all duration-300">
                    Team Collaboration
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                    Work seamlessly with your team. <span className="text-gray-200">Share projects</span>, maintain brand consistency, and collaborate in real-time with powerful agency features.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {[
                      { icon: '👥', text: 'Multi-User' },
                      { icon: '🏷️', text: 'Brand Assets' },
                      { icon: '🔄', text: 'Real-time Sync' }
                    ].map((tag) => (
                      <span key={tag.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-xs font-medium text-gray-300 group-hover:border-indigo-500/50 group-hover:text-indigo-400 group-hover:from-indigo-900/20 group-hover:to-indigo-800/20 transition-all duration-300">
                        <span className="text-sm">{tag.icon}</span>
                        {tag.text}
                      </span>
                    ))}
            </div>
          </div>
        </div>
      </div>

          </div>
          
          {/* Bottom CTA */}
          <div className="mt-20">
            <CTASection 
              className="animate-fade-in-up animation-delay-1600"
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
            />
          </div>
        </div>
      </div>

      {/* TEMPLATE CATEGORIES SHOWCASE - Premium Redesign */}
      <div className="relative py-32 bg-gradient-to-b from-black via-gray-950 to-black overflow-hidden">
        {/* Dynamic background elements */}
        <div className="absolute inset-0">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-950/30 via-black to-blue-950/30"></div>
          
          {/* Animated grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div 
              className="absolute inset-0" 
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(139, 92, 246, 0.3) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
                animation: 'subtle-shift 25s ease-in-out infinite'
              }}
            ></div>
          </div>
          
          {/* Floating orbs */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-600 rounded-full mix-blend-screen filter blur-[180px] opacity-20 animate-float"></div>
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-600 rounded-full mix-blend-screen filter blur-[180px] opacity-20 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-20">
            {/* Badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-full mb-8 animate-fade-in-down">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-purple-300 font-medium uppercase tracking-wider">200+ Premium Templates</span>
                <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse animation-delay-1000"></div>
              </div>
            </div>
            
            {/* Headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight animate-fade-in-up">
              Whatever You Sell,
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 animate-gradient-x">
                We Have The Perfect
              </span>
              <span className="text-white">Mockup For It</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Stop using the same boring templates as everyone else. Our library of 
              <span className="text-white font-semibold"> 200+ AI-enhanced scenes </span>
              across 11 categories gives you endless creative possibilities.
            </p>
          </div>
          
          {/* Categories grid - Premium design */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              { emoji: '📱', title: 'Digital & Technology', gradient: 'from-blue-600 to-cyan-600', delay: 400 },
              { emoji: '👕', title: 'Apparel & Wearables', gradient: 'from-purple-600 to-pink-600', delay: 500 },
              { emoji: '🖨️', title: 'Print & Stationery', gradient: 'from-orange-600 to-red-600', delay: 600 },
              { emoji: '📦', title: 'Packaging & Products', gradient: 'from-green-600 to-emerald-600', delay: 700 },
              { emoji: '🏢', title: 'Outdoor & Signage', gradient: 'from-indigo-600 to-blue-600', delay: 800 },
              { emoji: '🎁', title: 'Promotional Merchandise', gradient: 'from-pink-600 to-rose-600', delay: 900 },
              { emoji: '📚', title: 'Publishing & Media', gradient: 'from-amber-600 to-orange-600', delay: 1000 },
              { emoji: '🍕', title: 'Food & Beverage', gradient: 'from-red-600 to-orange-600', delay: 1100 },
              { emoji: '🏠', title: 'Home & Office Decor', gradient: 'from-teal-600 to-cyan-600', delay: 1200 },
              { emoji: '🎪', title: 'Events & Exhibitions', gradient: 'from-purple-600 to-indigo-600', delay: 1300 },
              { emoji: '💼', title: 'Business Materials', gradient: 'from-gray-600 to-gray-800', delay: 1400 },
              { emoji: '✨', title: '...and so much more!', gradient: 'from-yellow-600 to-pink-600', delay: 1500 }
            ].map((category, index) => (
              <div 
                key={category.title} 
                className="group relative animate-scale-in"
                style={{ animationDelay: `${category.delay}ms` }}
              >
                {/* Card container */}
                <div className="relative h-full">
                  {/* Hover glow effect */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${category.gradient} rounded-2xl opacity-0 group-hover:opacity-60 blur-lg transition-all duration-500`}></div>
                  
                  {/* Card content */}
                  <div className="relative h-full bg-gradient-to-br from-gray-900/90 to-gray-950/90 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 transform group-hover:scale-105 transition-all duration-300 overflow-hidden">
                    {/* Internal gradient on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                    
                    {/* Emoji container with animation */}
                    <div className="relative mb-4">
                      <div className="text-5xl transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        {category.emoji}
                      </div>
                      {/* Pulse effect on hover */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`w-16 h-16 bg-gradient-to-r ${category.gradient} rounded-full opacity-0 group-hover:opacity-30 blur-xl animate-ping`}></div>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-bold text-white text-center group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-all duration-300">
                      {category.title}
                    </h3>
                    
                    {/* Hover indicator */}
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Bottom section with interactive preview */}
          <div className="mt-20 text-center">
            {/* Stats row */}
            <div className="flex flex-wrap justify-center gap-8 mb-12 animate-fade-in-up animation-delay-1600">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">11</span>
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-white">Categories</div>
                  <div className="text-sm text-gray-400">Every niche covered</div>
        </div>
      </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">200+</span>
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-white">Templates</div>
                  <div className="text-sm text-gray-400">Professional designs</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">∞</span>
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-white">Possibilities</div>
                  <div className="text-sm text-gray-400">Unlimited creativity</div>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            <CTASection 
              className="animate-fade-in-up animation-delay-1800"
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
            />
          </div>
        </div>
      </div>

      {/* CREATIVE IDEAS SECTION - Premium Redesign */}
      <div className="relative py-32 bg-black overflow-hidden">
        {/* Dynamic background with depth */}
        <div className="absolute inset-0">
          {/* Multi-layer gradients */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/40 via-purple-950/30 to-pink-950/40"></div>
          
          {/* Animated pattern */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(30deg, transparent 0%, rgba(59, 130, 246, 0.1) 50%, transparent 100%),
                linear-gradient(-30deg, transparent 0%, rgba(168, 85, 247, 0.1) 50%, transparent 100%)
              `,
              backgroundSize: '100px 100px',
              animation: 'subtle-shift 30s ease-in-out infinite'
            }}></div>
          </div>
          
          {/* Creative floating elements */}
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-blue-600 rounded-full mix-blend-screen filter blur-[150px] opacity-25 animate-float"></div>
          <div className="absolute bottom-20 right-1/3 w-96 h-96 bg-purple-600 rounded-full mix-blend-screen filter blur-[180px] opacity-20 animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-3/4 w-80 h-80 bg-pink-600 rounded-full mix-blend-screen filter blur-[160px] opacity-20 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Section header with enhanced design */}
          <div className="text-center mb-20">
            {/* Animated badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-full mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
              </svg>
              <span className="text-sm text-blue-300 font-medium uppercase tracking-wider">Creative Inspiration</span>
            </div>
            
            {/* Main headline with gradient effect */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight animate-fade-in-up">
              Unlock an
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 animate-gradient-x">
                Infinite Library
              </span>
              <span className="text-white">Of Marketing Creatives</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Stop wondering what to post or how to design your next launch. 
              <span className="text-white font-semibold"> Transform these ideas </span>
              into reality in the next 5 minutes.
            </p>
          </div>
          
          {/* Creative Ideas Cards - Premium 3D Design */}
          <div className="grid lg:grid-cols-3 gap-8 md:gap-10">
            
            {/* Product Launch Card */}
            <div className="group relative animate-fade-in-up animation-delay-400" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5" style={{ transformStyle: 'preserve-3d' }}>
                {/* Glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                {/* Card content */}
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full overflow-hidden">
                  {/* Background gradient on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-600/5 to-red-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Icon header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-orange-500 blur-xl opacity-50 group-hover:opacity-75 animate-pulse"></div>
                      <div className="relative w-16 h-16 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <span className="text-3xl">🚀</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-orange-400 group-hover:to-red-400 transition-all duration-300">
                      For Your Next<br />Product Launch
                    </h3>
                  </div>
                  
                  {/* Ideas list with hover effects */}
                  <ul className="space-y-4">
                    {[
                      'Create a "bundle" shot showing your main offer and bonuses on different devices',
                      'Generate mockups of your software inside a physical product box',
                      'Visualize your digital download as a stunning hardcover book'
                    ].map((idea, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="flex-shrink-0 w-6 h-6 mt-1 rounded-full bg-gradient-to-br from-orange-600/20 to-red-600/20 flex items-center justify-center mr-3 group-hover/item:from-orange-600/40 group-hover/item:to-red-600/40 transition-all duration-300">
                          <div className="w-2 h-2 bg-gradient-to-br from-orange-400 to-red-400 rounded-full"></div>
                        </div>
                        <span className="text-gray-400 text-sm leading-relaxed group-hover/item:text-gray-300 transition-colors duration-300">
                          {idea}
                        </span>
                      </li>
                    ))}
              </ul>
                  
                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-600 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </div>
            </div>
            
            {/* Social Media Card */}
            <div className="group relative animate-fade-in-up animation-delay-600" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5" style={{ transformStyle: 'preserve-3d' }}>
                {/* Glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                {/* Card content */}
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full overflow-hidden">
                  {/* Background gradient on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Icon header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-blue-500 blur-xl opacity-50 group-hover:opacity-75 animate-pulse"></div>
                      <div className="relative w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <span className="text-3xl">📱</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300">
                      For Your Social<br />Media Marketing
                    </h3>
                  </div>
                  
                  {/* Ideas list with hover effects */}
                  <ul className="space-y-4">
                    {[
                      'Put your brand logo on t-shirts, mugs, and tote bags for viral content',
                      'Showcase customer testimonials inside stylish phone mockups',
                      'Announce your webinar on a giant urban billboard mockup'
                    ].map((idea, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="flex-shrink-0 w-6 h-6 mt-1 rounded-full bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center mr-3 group-hover/item:from-blue-600/40 group-hover/item:to-purple-600/40 transition-all duration-300">
                          <div className="w-2 h-2 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full"></div>
                        </div>
                        <span className="text-gray-400 text-sm leading-relaxed group-hover/item:text-gray-300 transition-colors duration-300">
                          {idea}
                        </span>
                      </li>
                    ))}
              </ul>
                  
                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </div>
            </div>
            
            {/* E-commerce Card */}
            <div className="group relative animate-fade-in-up animation-delay-800" style={{ perspective: '1000px' }}>
              <div className="relative transform-gpu transition-all duration-500 group-hover:rotate-y-5" style={{ transformStyle: 'preserve-3d' }}>
                {/* Glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-70 blur-lg transition-all duration-500"></div>
                
                {/* Card content */}
                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full overflow-hidden">
                  {/* Background gradient on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 to-emerald-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Icon header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-green-500 blur-xl opacity-50 group-hover:opacity-75 animate-pulse"></div>
                      <div className="relative w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <span className="text-3xl">🛒</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-emerald-400 transition-all duration-300">
                      For Your<br />E-commerce Store
                    </h3>
                  </div>
                  
                  {/* Ideas list with hover effects */}
                  <ul className="space-y-4">
                    {[
                      'Create consistent, professional looks for all your product listings',
                      'Use AI Background Remover for clean, white-background shots',
                      'Display designs on apparel without printing a single item'
                    ].map((idea, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="flex-shrink-0 w-6 h-6 mt-1 rounded-full bg-gradient-to-br from-green-600/20 to-emerald-600/20 flex items-center justify-center mr-3 group-hover/item:from-green-600/40 group-hover/item:to-emerald-600/40 transition-all duration-300">
                          <div className="w-2 h-2 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full"></div>
                        </div>
                        <span className="text-gray-400 text-sm leading-relaxed group-hover/item:text-gray-300 transition-colors duration-300">
                          {idea}
                        </span>
                      </li>
                    ))}
              </ul>
                  
                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-600 to-emerald-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </div>
          </div>
        </div>
            
      </div>

          {/* Bottom CTA Section */}
          <div className="text-center mt-20">
            <p className="text-lg text-gray-400 mb-8 animate-fade-in-up animation-delay-1000">
              These are just a few examples. With BrandScapes AI, your creativity is the only limit.
            </p>
            
            <CTASection 
              className="animate-fade-in-up animation-delay-1200"
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
            />
          </div>
        </div>
      </div>

      {/* COMPARISON TABLE - Premium Redesign */}
      <div className="relative py-32 bg-gradient-to-b from-black via-gray-950 to-black overflow-hidden">
        {/* Dynamic background elements */}
        <div className="absolute inset-0">
          {/* Gradient mesh */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-950/20 via-black to-green-950/20"></div>
          
          {/* Animated grid lines */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(to right, rgba(239, 68, 68, 0.3) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(34, 197, 94, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              animation: 'subtle-shift 35s ease-in-out infinite'
            }}></div>
          </div>
          
          {/* Floating orbs - Red and Green theme */}
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-red-600 rounded-full mix-blend-screen filter blur-[200px] opacity-15 animate-float"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-green-600 rounded-full mix-blend-screen filter blur-[200px] opacity-15 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-20">
            {/* Badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-red-600/20 to-green-600/20 border border-yellow-500/30 rounded-full mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-yellow-300 font-medium uppercase tracking-wider">Workflow Revolution</span>
            </div>
            
            {/* Main headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight animate-fade-in-up">
              Stop Wasting Time & Money
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 animate-gradient-x">
                The "Old Way"
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              See how <span className="text-white font-semibold">BrandScapes AI</span> eliminates the costs, delays, 
              and frustrations of traditional design workflows <span className="text-white font-semibold">forever</span>.
            </p>
          </div>
          
          {/* Comparison Table - Premium Design */}
          <div className="relative max-w-6xl mx-auto">
            {/* VS Badge in the middle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 hidden md:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-yellow-600 blur-xl opacity-75 animate-pulse"></div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-orange-600 to-yellow-600 rounded-full flex items-center justify-center transform rotate-12 shadow-2xl">
                  <span className="text-2xl font-black text-white transform -rotate-12">VS</span>
                  </div>
                  </div>
                  </div>
            
            <div className="grid md:grid-cols-2 gap-8 md:gap-16">
              
              {/* Old Workflow - Left Side */}
              <div className="group animate-fade-in-left animation-delay-400">
                <div className="relative h-full">
                  {/* Red glow effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-2xl opacity-50 blur-lg"></div>
                  
                  {/* Card content */}
                  <div className="relative h-full bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-red-900/50 rounded-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-red-900/50 to-red-800/50 p-8 text-center border-b border-red-800/30">
                      <div className="flex items-center justify-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-red-600/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">😞</span>
                  </div>
                        <h3 className="text-3xl font-bold text-red-400">Your Old Workflow</h3>
                  </div>
                      <p className="text-red-300/60 text-sm">The painful reality of traditional design</p>
                </div>
                    
                    {/* Pain points */}
                    <div className="p-8 space-y-6">
                      {[
                        { icon: '🐢', text: 'Idea → Wait 1 week for designer', highlight: '1 week' },
                        { icon: '💰', text: 'Pay $50-$150 per mockup', highlight: '$50-$150' },
                        { icon: '😩', text: 'Endless frustrating revisions', highlight: 'Endless' },
                        { icon: '🤷‍♂️', text: 'Inconsistent quality & results', highlight: 'Inconsistent' },
                        { icon: '🐌', text: 'Your best ideas die from friction', highlight: 'die' }
                      ].map((item, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-4 group/item"
                          style={{ animationDelay: `${600 + index * 100}ms` }}
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-red-900/30 rounded-xl flex items-center justify-center transform group-hover/item:scale-110 transition-transform">
                            <span className="text-2xl">{item.icon}</span>
                          </div>
                          <p className="text-gray-300 flex-1">
                            {item.text.split(item.highlight).map((part, i) => (
                              <span key={i}>
                                {part}
                                {i === 0 && (
                                  <span className="text-red-400 font-semibold mx-1">{item.highlight}</span>
                                )}
                              </span>
                            ))}
                          </p>
                          <div className="w-8 h-8 bg-red-900/20 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      ))}
              </div>
              
                    {/* Bottom stats */}
                    <div className="bg-red-950/30 p-6 border-t border-red-900/30">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-red-400">$3,600</div>
                          <div className="text-xs text-red-300/60">Per Year</div>
                  </div>
                        <div>
                          <div className="text-2xl font-bold text-red-400">168 hrs</div>
                          <div className="text-xs text-red-300/60">Wasted Waiting</div>
                  </div>
                        <div>
                          <div className="text-2xl font-bold text-red-400">50%</div>
                          <div className="text-xs text-red-300/60">Ideas Lost</div>
                  </div>
                  </div>
                  </div>
                </div>
              </div>
            </div>
              
              {/* New BrandScapes AI Workflow - Right Side */}
              <div className="group animate-fade-in-right animation-delay-400">
                <div className="relative h-full">
                  {/* Green glow effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl opacity-50 blur-lg"></div>
                  
                  {/* Card content */}
                  <div className="relative h-full bg-gradient-to-br from-gray-900/95 to-gray-950/95 backdrop-blur-xl border border-green-900/50 rounded-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-900/50 to-emerald-800/50 p-8 text-center border-b border-green-800/30">
                      <div className="flex items-center justify-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">🚀</span>
          </div>
                        <h3 className="text-3xl font-bold text-green-400">BrandScapes AI</h3>
        </div>
                      <p className="text-green-300/60 text-sm">The future of design automation</p>
      </div>

                    {/* Benefits */}
                    <div className="p-8 space-y-6">
                      {[
                        { icon: '⚡', text: 'Idea → Mockup ready in 30 seconds', highlight: '30 seconds' },
                        { icon: '💎', text: 'One-time payment for lifetime access', highlight: 'One-time' },
                        { icon: '♾️', text: 'Generate unlimited variations instantly', highlight: 'unlimited' },
                        { icon: '✨', text: 'Perfect, studio-quality results every time', highlight: 'Perfect' },
                        { icon: '🎯', text: 'Launch ideas the same day you have them', highlight: 'same day' }
                      ].map((item, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-4 group/item animate-fade-in-up"
                          style={{ animationDelay: `${600 + index * 100}ms` }}
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-green-900/30 rounded-xl flex items-center justify-center transform group-hover/item:scale-110 group-hover/item:rotate-3 transition-all">
                            <span className="text-2xl">{item.icon}</span>
                          </div>
                          <p className="text-gray-300 flex-1">
                            {item.text.split(item.highlight).map((part, i) => (
                              <span key={i}>
                                {part}
                                {i === 0 && (
                                  <span className="text-green-400 font-semibold mx-1">{item.highlight}</span>
                                )}
                              </span>
                            ))}
                          </p>
                          <div className="w-8 h-8 bg-green-900/20 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      ))}
          </div>
          
                    {/* Bottom stats */}
                    <div className="bg-green-950/30 p-6 border-t border-green-900/30">
                      <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                          <div className="text-2xl font-bold text-green-400">$299</div>
                          <div className="text-xs text-green-300/60">Once Forever</div>
                </div>
                        <div>
                          <div className="text-2xl font-bold text-green-400">5 min</div>
                          <div className="text-xs text-green-300/60">Total Time</div>
              </div>
                        <div>
                          <div className="text-2xl font-bold text-green-400">100%</div>
                          <div className="text-xs text-green-300/60">Ideas Realized</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            
                </div>
              </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-20">
            <p className="text-lg text-gray-400 mb-8 animate-fade-in-up animation-delay-1200">
              Stop letting design bottlenecks kill your best ideas. Join thousands who've made the switch.
            </p>
            
            <div className="inline-block relative group animate-fade-in-up animation-delay-1400">
              
              
              <CTASection 
                className=""
                showHeadline={true}
                showRating={true}
                showPaymentImage={true}
              />
            </div>
          </div>
        </div>
            </div>
            

      {/* TESTIMONIALS SECTION - Premium Light Design */}
      <div className="relative py-32 bg-gradient-to-b from-gray-50 via-white to-gray-50 overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(99, 102, 241, 0.06) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}></div>
                </div>
          
          {/* Soft gradient orbs */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float animation-delay-4000"></div>
              </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-20">
            {/* Animated badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-full mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm text-blue-700 font-medium uppercase tracking-wider">Success Stories</span>
            </div>
            
            {/* Main headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-8 leading-tight animate-fade-in-up">
              See Why
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 animate-gradient-x">
                10,000+ Marketers
              </span>
              <span className="text-gray-900">Have Made The Switch</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Join thousands of smart marketers who've transformed their workflow with 
              <span className="text-gray-900 font-semibold"> BrandScapes AI</span>. 
              Here's what they're saying...
              </p>
            </div>
            
          {/* Testimonials Grid - Premium Cards */}
          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            
            {/* Testimonial 1 */}
            <div className="group animate-fade-in-up animation-delay-400">
              <div className="relative h-full">
                {/* Gradient border effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500"></div>
                
                {/* Card content */}
                <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl transform group-hover:-translate-y-1 transition-all duration-300">
                  {/* Quote icon */}
                  <div className="absolute -top-4 -left-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
          </div>
        </div>
                  
                  {/* Rating stars */}
                  <div className="flex mb-6">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
      </div>

                  {/* Testimonial text */}
                  <p className="text-gray-700 text-lg leading-relaxed mb-8">
                    "I was spending <span className="font-semibold text-gray-900">$500/month</span> on mockups for my product launches. 
                    BrandScapes AI cut that to a <span className="font-semibold text-gray-900">one-time $299</span> and the quality is even better. 
                    The ROI was immediate."
                  </p>
                  
                  {/* Author info */}
                  <div className="flex items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                      SC
                    </div>
                <div>
                      <h4 className="font-bold text-gray-900 text-lg">Sarah Chen</h4>
                      <p className="text-sm text-gray-600">E-commerce Entrepreneur</p>
                </div>
          </div>
          
                  {/* Impact badge */}
                  <div className="absolute -bottom-3 right-6">
                    <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg">
                      95% Cost Reduction
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="group animate-fade-in-up animation-delay-600">
              <div className="relative h-full">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500"></div>
                
                <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl transform group-hover:-translate-y-1 transition-all duration-300">
                  <div className="absolute -top-4 -left-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex mb-6">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-gray-700 text-lg leading-relaxed mb-8">
                    "BrandScapes AI saved our agency <span className="font-semibold text-gray-900">$60K annually</span>. 
                    We went from <span className="font-semibold text-gray-900">2-week turnarounds</span> to 
                    <span className="font-semibold text-gray-900"> same-day delivery</span>. 
                    Client satisfaction is through the roof!"
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                      MK
            </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Mike Kennedy</h4>
                      <p className="text-sm text-gray-600">Creative Agency Director</p>
                    </div>
            </div>
            
                  <div className="absolute -bottom-3 right-6">
                    <div className="bg-gradient-to-r from-purple-400 to-pink-500 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg">
                      14x Faster Delivery
                </div>
                  </div>
                  </div>
                </div>
              </div>
              
            {/* Testimonial 3 */}
            <div className="group animate-fade-in-up animation-delay-800">
              <div className="relative h-full">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500"></div>
                
                <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl transform group-hover:-translate-y-1 transition-all duration-300">
                  <div className="absolute -top-4 -left-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                  </div>
                  </div>
                  
                  <div className="flex mb-6">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-gray-700 text-lg leading-relaxed mb-8">
                    "The white-label feature lets us offer mockup services to clients. 
                    Added <span className="font-semibold text-gray-900">$25K monthly revenue</span> with 
                    <span className="font-semibold text-gray-900"> zero additional overhead</span>."
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                      LR
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Lisa Rodriguez</h4>
                      <p className="text-sm text-gray-600">Marketing Agency Owner</p>
                </div>
              </div>
              
                  <div className="absolute -bottom-3 right-6">
                    <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg">
                      +$25K Monthly Revenue
                </div>
                  </div>
                  </div>
                </div>
              </div>
            
            </div>
            
          {/* Success metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto animate-fade-in-up animation-delay-1000">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">10,000+</div>
              <div className="text-sm text-gray-600">Happy Users</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">4.9/5</div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">$5M+</div>
              <div className="text-sm text-gray-600">Saved Collectively</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">30 sec</div>
              <div className="text-sm text-gray-600">Avg. Creation Time</div>
            </div>
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-20 animate-fade-in-up animation-delay-1200">
            <p className="text-lg text-gray-600 mb-8">
              Ready to join thousands of successful marketers?
            </p>
            
            <CTASection 
              className=""
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
              useDarkPaymentImage={true} 
              darkMode={true} 
            />
            </div>
          </div>
        </div>

      {/* PRICING SECTION - Premium Light Design */}
      <div id="pricing" className="relative py-32 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0">
          {/* Gradient mesh pattern */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(45deg, rgba(59, 130, 246, 0.05) 25%, transparent 25%),
                linear-gradient(-45deg, rgba(59, 130, 246, 0.05) 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, rgba(59, 130, 246, 0.05) 75%),
                linear-gradient(-45deg, transparent 75%, rgba(59, 130, 246, 0.05) 75%)
              `,
              backgroundSize: '50px 50px',
              backgroundPosition: '0 0, 0 25px, 25px -25px, -25px 0px'
            }}></div>
      </div>

          {/* Floating gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-20">
            {/* Limited time badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-red-100 to-orange-100 border border-red-200 rounded-full mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-red-600 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-red-700 font-medium uppercase tracking-wider">Limited Time Offer</span>
            </div>
            
            {/* Main headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-8 leading-tight animate-fade-in-up">
              <span className="block">Get BrandScapes AI</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 animate-gradient-x">
                Pay Once. Use Forever.
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Join thousands who've already secured lifetime access before the price increases to 
              <span className="text-gray-900 font-semibold"> $67/month</span>
            </p>
          </div>
          
          {/* Single Pricing Card - Premium Design */}
          <div className="relative max-w-2xl mx-auto animate-scale-in animation-delay-400">
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl opacity-20 blur-2xl animate-pulse"></div>
            
            {/* Main pricing card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top section with gradient */}
              <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-10 text-center">
                {/* Decorative pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
                
                {/* Plan name and badge */}
                <div className="relative">
                  <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                    <svg className="w-5 h-5 text-yellow-300 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-white font-bold">PROFESSIONAL LIFETIME ACCESS</span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-white mb-4">Transform Your Marketing Forever</h3>
                  
                  {/* Pricing */}
                  <div className="flex items-center justify-center gap-4 mb-2">
                    <span className="text-gray-300 line-through text-3xl">$67</span>
                <div className="text-center">
                      <div className="text-7xl font-black text-white">$37</div>
                      <div className="text-sm text-gray-200 uppercase tracking-wider">One-Time Payment</div>
                  </div>
                  </div>
                  
                  {/* Savings badge */}
                  <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full text-white font-bold shadow-lg">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    SAVE 45% TODAY
                  </div>
                </div>
              </div>
              
              {/* Features section */}
              <div className="p-10">
                <h4 className="text-2xl font-bold text-gray-900 text-center mb-8">Everything You Need to Create Stunning Mockups</h4>
                
                {/* Features grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-10">
                  {[
                    { icon: '♾️', title: 'Unlimited Mockups', desc: 'Create as many as you need, forever' },
                    { icon: '🎨', title: '200+ Premium Templates', desc: 'Professional designs for every niche' },
                    { icon: '⚡', title: '30-Second Generation', desc: 'From upload to download instantly' },
                    { icon: '📸', title: '4K Quality Exports', desc: 'Crystal clear, print-ready resolution' },
                    { icon: '🎯', title: 'AI Background Removal', desc: 'One-click professional cutouts' },
                    { icon: '🚀', title: 'Lifetime Updates', desc: 'All future features included' },
                    { icon: '💎', title: 'No Watermarks', desc: 'Clean, professional results' },
                    { icon: '🛡️', title: 'Commercial License', desc: 'Use for clients and resale' }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start group">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                        <span className="text-2xl">{feature.icon}</span>
                  </div>
                      <div>
                        <h5 className="font-bold text-gray-900 mb-1">{feature.title}</h5>
                        <p className="text-sm text-gray-600">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
                {/* Value comparison - Premium Design */}
                <div className="relative rounded-2xl overflow-hidden mb-10">
                  {/* Gradient border */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black p-[1px] rounded-2xl">
                    <div className="absolute inset-[1px] bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-2xl"></div>
              </div>
                  
                  <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-2xl p-8">
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Value Comparison</h4>
            </div>
            
                    <div className="space-y-4">
                      {/* Designer Cost */}
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-red-100 to-orange-100 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between group-hover:border-red-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                              <span className="text-xl">👨‍🎨</span>
              </div>
                            <div>
                              <div className="text-sm text-gray-500 font-medium">Designer Cost</div>
                              <div className="text-xs text-gray-400">10 mockups/month</div>
            </div>
          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-400 line-through">$500+</div>
                            <div className="text-xs text-gray-400">per month</div>
                          </div>
        </div>
      </div>

                      {/* Monthly Subscription */}
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between group-hover:border-orange-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                              <span className="text-xl">📅</span>
                            </div>
                            <div>
                              <div className="text-sm text-gray-500 font-medium">Monthly Tools</div>
                              <div className="text-xs text-gray-400">Recurring forever</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-400 line-through">$67</div>
                            <div className="text-xs text-gray-400">per month</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* BrandScapes AI - Winner */}
                      <div className="relative">
                        {/* Glow effect */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 rounded-xl blur-sm opacity-75 animate-pulse"></div>
                        <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-5 transform hover:scale-[1.02] transition-transform">
                          <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-bounce">
                            BEST VALUE
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center shadow-lg">
                                <span className="text-2xl">🚀</span>
                              </div>
                              <div>
                                <div className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                  BrandScapes AI
                                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                  </svg>
                                </div>
                                <div className="text-xs text-green-700 font-medium">Unlimited forever</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-black bg-gradient-to-r from-green-600 to-emerald-600 text-transparent bg-clip-text">$37</div>
                              <div className="text-xs font-bold text-green-700">ONE TIME</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom savings indicator */}
                    <div className="mt-6 text-center p-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg">
                      <p className="text-sm font-medium text-green-800">
                        You save <span className="font-bold text-green-900">$5,967+</span> in the first year alone!
                      </p>
                    </div>
                    
                    {/* Device Mockup Image */}
                    <div className="mt-6 text-center">
                      <img 
                        src="/box-laptop-tablet-phone.png" 
                        alt="Multiple devices showing BrandScapes AI" 
                        className="mx-auto w-full max-w-md rounded-lg shadow-lg"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Main CTA button - Premium Dark Box with Enhanced Animation */}
                <div className="relative rounded-3xl overflow-hidden mb-4">
                  {/* Darker multi-layer background */}
                  <div className="absolute inset-0 bg-black"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-black to-gray-950 opacity-90"></div>
                  
                  {/* Enhanced animated glow layers */}
                  <div className="absolute inset-0">
                    {/* Primary pulse ring */}
                    <div className="absolute inset-0 rounded-3xl" 
                      style={{
                        background: 'conic-gradient(from 0deg at 50% 50%, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #3b82f6)',
                        opacity: 0.3,
                        animation: 'spin 8s linear infinite, pulse 2s ease-in-out infinite'
                      }}
                    ></div>
                    
                    {/* Secondary pulse ring */}
                    <div className="absolute inset-2 rounded-3xl bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-20"
                      style={{
                        animation: 'pulse 3s ease-in-out infinite',
                        animationDelay: '0.5s'
                      }}
                    ></div>
                    
                    {/* Breathing glow effect */}
                    <div className="absolute inset-4 rounded-3xl"
                      style={{
                        background: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.3), transparent 70%)',
                        animation: 'pulse 4s ease-in-out infinite',
                        animationDelay: '1s'
                      }}
                    ></div>
                  </div>
                  
                  {/* Border gradient animation */}
                  <div className="absolute inset-0 p-[2px] rounded-3xl">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
                      style={{
                        animation: 'gradient-x 3s ease infinite'
                      }}
                    ></div>
                    <div className="absolute inset-[2px] bg-black rounded-3xl"></div>
                  </div>
                  
                  {/* Premium corner effects */}
                  <div className="absolute -top-20 -left-20 w-40 h-40 bg-blue-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"></div>
                  <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse animation-delay-1000"></div>
                  
                  {/* Sparkle effects */}
                  <div className="absolute inset-0 overflow-hidden">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={`sparkle-${i}`}
                        className="absolute w-0.5 h-0.5 bg-white rounded-full"
                        style={{
                          left: `${10 + i * 10}%`,
                          top: `${20 + (i % 3) * 20}%`,
                          animation: `ping ${2 + i * 0.5}s ease-in-out infinite`,
                          animationDelay: `${i * 0.2}s`,
                          opacity: 0.6
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Content container with padding */}
                  <div className="relative z-10 p-8">
                    {/* Urgency banner */}
                    <div className="text-center mb-4">
                      <div className="inline-flex items-center gap-2 bg-red-900/30 text-red-300 px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm border border-red-800/50">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                        </span>
                        Limited Time: Save $30 Today Only
                      </div>
                    </div>
                    
                    <CTASection 
                      className=""
                      showHeadline={true}
                      showRating={true}
                      showPaymentImage={true}
                    />
                  </div>
                </div>
                
                {/* Trust badges */}
                <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">30-Day Money Back Guarantee</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                    <span className="text-sm">Join 10,000+ Users</span>
                  </div>
                </div>
          </div>
        </div>
      </div>

          {/* Urgency reminder */}
          <div className="text-center mt-16 animate-fade-in-up animation-delay-600">
            <div className="inline-flex items-center px-6 py-3 bg-red-100 rounded-full">
              <svg className="w-5 h-5 text-red-600 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700 font-semibold">
                Price increases to $67/month after this launch period
              </span>
            </div>
          </div>
        </div>
      </div>

 {/* FAST ACTION BONUSES - Premium Gradient Design */}
 <div className="relative py-32 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 overflow-hidden">
        {/* Dynamic background elements */}
        <div className="absolute inset-0">
          {/* Animated patterns */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 3px 3px, rgba(251, 146, 60, 0.3) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
              animation: 'subtle-shift 25s ease-in-out infinite'
            }}></div>
          </div>
          
          {/* Floating gift elements */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-3/4 w-72 h-72 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-20">
            {/* Bonus badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-orange-100 to-red-100 border border-orange-300 rounded-full mb-8 animate-fade-in-down">
              <svg className="w-5 h-5 text-orange-600 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
              </svg>
              <span className="text-sm text-orange-700 font-medium uppercase tracking-wider">Exclusive Bonuses</span>
            </div>
            
            {/* Main headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-8 leading-tight animate-fade-in-up">
              But Wait... There's
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 animate-gradient-x">
                Even More!
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Get instant access to these <span className="font-semibold text-gray-900">4 Fast-Action Bonuses</span> 
              <br />
              <span className="text-3xl font-bold text-orange-600">(Total Value: $588)</span> when you act now
            </p>
          </div>
          
          {/* Bonuses Grid - Premium Cards */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            
            {/* Bonus 1 */}
            <div className="group relative animate-fade-in-up animation-delay-400">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl opacity-30 group-hover:opacity-100 blur-lg transition-all duration-500"></div>
              <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl overflow-hidden">
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                
                {/* Number badge */}
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg transform rotate-3">
                  1
                </div>
                </div>
                
                {/* Content */}
                <div className="relative">
                  <div className="mb-4">
                    <span className="text-4xl">🎨</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    The "Don't Have a Design?" Starter Pack
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Get a curated collection of 50 professionally designed, fully-customizable logos and design elements 
                    for various niches. Perfect for getting started immediately.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Instant download</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 line-through">$197</span>
                      <span className="text-2xl font-bold text-orange-600">FREE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bonus 2 */}
            <div className="group relative animate-fade-in-up animation-delay-600">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl opacity-30 group-hover:opacity-100 blur-lg transition-all duration-500"></div>
              <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100 to-red-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg transform rotate-3">
                  2
                </div>
                </div>
                
                <div className="relative">
                  <div className="mb-4">
                    <span className="text-4xl">📈</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    The "Triple Your CTR" Mockup Ad Formula
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    A comprehensive 3-part video training showing you exactly how to use your mockups 
                    to create scroll-stopping, high-converting ads that demand attention.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">3 HD video lessons</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 line-through">$147</span>
                      <span className="text-2xl font-bold text-red-600">FREE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bonus 3 */}
            <div className="group relative animate-fade-in-up animation-delay-800">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-pink-400 rounded-2xl opacity-30 group-hover:opacity-100 blur-lg transition-all duration-500"></div>
              <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-100 to-pink-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg transform rotate-3">
                  3
                </div>
                </div>
                
                <div className="relative">
                  <div className="mb-4">
                    <span className="text-4xl">🚀</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    The Product Launch Visuals Blueprint
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    This comprehensive checklist walks you through the 12 essential mockups you need 
                    for any successful product launch, plus timing strategies for maximum impact.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">PDF + Templates</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 line-through">$97</span>
                      <span className="text-2xl font-bold text-pink-600">FREE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bonus 4 */}
            <div className="group relative animate-fade-in-up animation-delay-1000">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-indigo-400 rounded-2xl opacity-30 group-hover:opacity-100 blur-lg transition-all duration-500"></div>
              <div className="relative h-full bg-white rounded-2xl p-8 shadow-xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg transform rotate-3">
                  4
                </div>
                </div>
                
                <div className="relative">
                  <div className="mb-4">
                    <span className="text-4xl">👥</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Founder's Private Community Access
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Join our exclusive founders-only community. Network with successful marketers, 
                    share strategies, get feedback, and access member-only resources.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Lifetime membership</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 line-through">$147/yr</span>
                      <span className="text-2xl font-bold text-purple-600">FREE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
            
      </div>

          {/* Total bonus value */}
          <div className="text-center mb-12 animate-fade-in-up animation-delay-1200">
            <div className="inline-block relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl blur-lg opacity-50 animate-pulse"></div>
              <div className="relative bg-white rounded-2xl px-8 py-6 shadow-xl">
                <p className="text-gray-600 mb-2">Total Bonus Value:</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl text-gray-400 line-through">$588</span>
                  <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
                    FREE TODAY
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Urgency message */}
          <div className="text-center animate-fade-in-up animation-delay-1400">
            <p className="text-lg text-gray-700 mb-8">
              These exclusive bonuses are only available during this special launch period.
              <br />
              <span className="font-semibold text-gray-900">Once the timer expires, they're gone forever.</span>
            </p>
            
            <CTASection 
              className=""
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
              useDarkPaymentImage={true} 
              darkMode={true} 
            />
          </div>
        </div>
      </div>

      {/* VALUE STACK SECTION - Mixed Light/Dark Theme */}
      <div className="relative py-32 bg-gray-900 overflow-hidden">
        {/* Dynamic background */}
        <div className="absolute inset-0">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/50 via-gray-900 to-purple-950/50"></div>
          
          {/* Animated grid */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.3) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(139, 92, 246, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              animation: 'subtle-shift 30s ease-in-out infinite'
            }}></div>
          </div>
          
          {/* Light orbs for contrast */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-screen filter blur-[200px] opacity-10 animate-float"></div>
          <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-purple-400 rounded-full mix-blend-screen filter blur-[200px] opacity-10 animate-float animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-20">
            {/* Value badge */}
            <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 border border-yellow-500/30 rounded-full mb-8 animate-fade-in-down">
              <svg className="w-4 h-4 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-yellow-300 font-medium uppercase tracking-wider">Total Value Stack</span>
            </div>
            
            {/* Main headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight animate-fade-in-up">
              Here's Everything In Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 animate-gradient-x">
                Founder's Pass
              </span>
          </h2>
          
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              We've bundled over <span className="text-white font-semibold">$4,435 of real-world value</span> into this 
              one-time offer, secured by your payment today.
            </p>
          </div>
          
          {/* Value Stack Cards - Mixed Theme Design */}
          <div className="relative">
            {/* Main container with light background */}
            <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 shadow-2xl">
              {/* Header with dark accent */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 mb-10 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">
                  When You Act Now, Your Founder's Investment Gets You:
            </h3>
                <p className="text-gray-300">Everything you need to dominate your market's visual game</p>
              </div>
              
              {/* Value items with alternating design */}
              <div className="space-y-4 mb-10">
                {[
                  { 
                    item: 'BrandScapes AI Lifetime License', 
                    value: '$67/month = $804/Year', 
                    icon: '🚀',
                    highlight: true,
                    savings: 'Save $767 Every Year'
                  },
                  { 
                    item: 'Full Access to 200+ Premium Templates', 
                    value: '$497',
                    icon: '🎨',
                    desc: 'Regularly updated with new designs'
                  },
                  { 
                    item: 'AI Background Removal Technology', 
                    value: '$19/month = $228/Year',
                    icon: '🎯',
                    desc: 'Powered by advanced AI'
                  },
                  { 
                    item: '4K Ultra HD & Print-Ready Exports', 
                    value: '$297',
                    icon: '📸',
                    desc: '300 DPI for perfect prints'
                  },
                  { 
                    item: 'Full Commercial & Client Rights', 
                    value: '$997',
                    icon: '💼',
                    desc: 'Sell to unlimited clients'
                  },
                  { 
                    item: 'Lifetime Updates & New Features', 
                    value: '$1,000+',
                    icon: '♾️',
                    desc: 'Forever access to everything new'
                  },
                  { 
                    item: 'Priority Support & Training', 
                    value: '$297',
                    icon: '🛡️',
                    desc: 'Get help when you need it'
                  }
                ].map((item, index) => (
                  <div 
                    key={item.item} 
                    className={`group relative animate-fade-in-up`}
                    style={{ animationDelay: `${400 + index * 100}ms` }}
                  >
                    <div className={`relative rounded-2xl overflow-hidden ${
                      item.highlight 
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 p-[2px]' 
                        : 'bg-gray-200 p-[1px]'
                    }`}>
                      <div className={`relative flex items-center justify-between p-6 rounded-2xl ${
                        item.highlight 
                          ? 'bg-gradient-to-r from-orange-50 to-red-50' 
                          : 'bg-white'
                      }`}>
                        {/* Left side with icon and content */}
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            item.highlight 
                              ? 'bg-gradient-to-br from-orange-500 to-red-500' 
                              : 'bg-gradient-to-br from-gray-700 to-gray-900'
                          }`}>
                            <span className="text-2xl">{item.icon}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 text-lg mb-1">{item.item}</h4>
                            {item.desc && (
                              <p className="text-sm text-gray-600">{item.desc}</p>
                            )}
                            {item.savings && (
                              <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold mt-2">
                                {item.savings}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Right side with value */}
                        <div className="text-right ml-4">
                          <div className={`font-bold text-xl ${
                            item.highlight ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {item.value}
                          </div>
                          <div className="text-xs text-gray-500 uppercase">Value</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Bonus Section Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      PLUS THESE FAST ACTION BONUSES
                    </span>
                  </div>
                </div>
                
                {/* Fast Action Bonuses */}
                {[
                  { 
                    item: 'BONUS: "Don\'t Have a Design?" Starter Pack', 
                    value: '$197',
                    icon: '🎁',
                    desc: '100 Logo Templates & Design Ideas',
                    isBonus: true,
                    highlight: false,
                    savings: undefined
                  },
                  { 
                    item: 'BONUS: "Triple Your CTR" Mockup Ad Formula', 
                    value: '$147',
                    icon: '📈',
                    desc: 'Facebook & Instagram Ad Templates',
                    isBonus: true,
                    highlight: false,
                    savings: undefined
                  },
                  { 
                    item: 'BONUS: Product Launch Visuals Blueprint', 
                    value: '$97',
                    icon: '🚀',
                    desc: 'Step-by-step Launch Framework',
                    isBonus: true,
                    highlight: false,
                    savings: undefined
                  },
                  { 
                    item: 'BONUS: Founder\'s Private Community Access', 
                    value: '$147/yr',
                    icon: '👥',
                    desc: 'Network with 10,000+ Marketers',
                    isBonus: true,
                    highlight: false,
                    savings: undefined
                  }
                ].map((item, index) => (
                  <div 
                    key={item.item} 
                    className={`group relative animate-fade-in-up`}
                    style={{ animationDelay: `${400 + index * 100}ms` }}
                  >
                    <div className={`relative rounded-2xl overflow-hidden ${
                      item.highlight 
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 p-[2px]' 
                        : item.isBonus
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 p-[2px]'
                        : 'bg-gray-200 p-[1px]'
                    }`}>
                      <div className={`relative flex items-center justify-between p-6 rounded-2xl ${
                        item.highlight 
                          ? 'bg-gradient-to-r from-orange-50 to-red-50' 
                          : item.isBonus
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50'
                          : 'bg-white'
                      }`}>
                        {/* Left side with icon and content */}
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            item.highlight 
                              ? 'bg-gradient-to-br from-orange-500 to-red-500' 
                              : item.isBonus
                              ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                              : 'bg-gradient-to-br from-gray-700 to-gray-900'
                          }`}>
                            <span className="text-2xl">{item.icon}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start gap-2">
                              <h4 className="font-bold text-gray-900 text-lg mb-1">{item.item}</h4>
                              {item.isBonus && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md text-xs font-bold">
                                  BONUS
                                </span>
                              )}
                            </div>
                            {item.desc && (
                              <p className="text-sm text-gray-600">{item.desc}</p>
                            )}
                            {item.savings && (
                              <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold mt-2">
                                {item.savings}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Right side with value */}
                        <div className="text-right ml-4">
                          <div className={`font-bold text-xl ${
                            item.highlight 
                              ? 'text-red-600' 
                              : item.isBonus
                              ? 'text-green-600'
                              : 'text-gray-900'
                          }`}>
                            {item.value}
                          </div>
                          <div className="text-xs text-gray-500 uppercase">Value</div>
                        </div>
                      </div>
                    </div>
                </div>
              ))}
            </div>
            
              {/* Total value section with dark theme */}
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-2xl blur-lg opacity-50"></div>
                <div className="relative bg-gray-900 rounded-2xl p-8">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-gray-400 uppercase text-sm mb-1">Total Real-World Value</div>
                      <div className="text-3xl font-black text-white">Everything Above</div>
                    </div>
                    <div className="text-right">
                      <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                        $4,435+
                      </div>
                      <div className="text-gray-400 text-sm">Total Package Value</div>
                    </div>
                  </div>
              </div>
            </div>
            
              {/* Get it all today - CTA section */}
              <div className="mt-10 text-center">
                <div className="relative inline-block">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl blur-lg opacity-75 animate-pulse"></div>
                  <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                    <h3 className="text-4xl font-black text-gray-900 mb-2">
                      Get It All Today For Just 
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> $37</span>
                    </h3>
                    <p className="text-gray-600 text-lg">One-time payment • Lifetime access • No hidden fees</p>
                    
                    <div className="mt-6">
                      <CTASection 
                        className=""
                        showHeadline={true}
                        showRating={true}
                        showPaymentImage={true}
                        useDarkPaymentImage={true} 
                        darkMode={true} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


     

      {/* PICTURE THIS SECTION - Premium Vision Design */}
      <div className="relative py-32 overflow-hidden">
        {/* Gradient background with depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900"></div>
        
        {/* Overlay gradients for richness */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0">
          {/* Floating dream orbs */}
          <div className="absolute top-10 left-10 w-96 h-96 bg-blue-500/20 rounded-full filter blur-[120px] animate-float"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500/20 rounded-full filter blur-[120px] animate-float animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/20 rounded-full filter blur-[120px] animate-float animation-delay-4000"></div>
        </div>
        
        {/* Sparkle effect */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={`sparkle-${i}`}
              className="absolute w-1 h-1 bg-white rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Vision badge */}
          <div className="flex justify-center mb-8 animate-fade-in-down">
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
              <span className="text-sm font-medium text-white uppercase tracking-wider">Your Future Reality</span>
            </div>
          </div>
          
          {/* Main headline with dramatic effect */}
          <h2 className="text-5xl md:text-7xl font-black text-center mb-16 leading-tight animate-fade-in-up">
            <span className="text-white">Picture This,</span>
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300">
              Just Minutes From Now...
            </span>
          </h2>
          
          {/* Vision cards - immersive storytelling */}
          <div className="space-y-8 mb-16">
            {/* Vision 1 */}
            <div className="relative group animate-fade-in-left animation-delay-200">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition duration-300"></div>
              <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 transform group-hover:scale-[1.02] transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">💡</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3">The Moment It Changes</h3>
                    <p className="text-lg text-gray-200 leading-relaxed">
                      An idea for a new promotion hits you. Instead of sighing and thinking about the cost and delay, you 
                      <span className="text-yellow-300 font-semibold"> smile</span>. You log into BrandScapes AI, upload a simple line of text, and in 
                      <span className="text-green-300 font-semibold"> under 30 seconds</span>, you have a dozen stunning, professional visuals ready to go.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Vision 2 */}
            <div className="relative group animate-fade-in-right animation-delay-400">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition duration-300"></div>
              <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 transform group-hover:scale-[1.02] transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">✨</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3">Your New Professional Standard</h3>
                    <p className="text-lg text-gray-200 leading-relaxed">
                      Your sales pages look like they were designed by a 
                      <span className="text-purple-300 font-semibold"> six-figure agency</span>. Your social media feed is a cascade of eye-catching, professional content. You launch new ideas 
                      <span className="text-blue-300 font-semibold"> the same day</span> you think of them. You feel confident, in control, and 
                      <span className="text-pink-300 font-semibold"> proud</span> of how your brand looks to the world.
                    </p>
                  </div>
          </div>
        </div>
      </div>

            {/* Vision 3 - The Reality */}
            <div className="relative group animate-fade-in-up animation-delay-600">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl blur-lg opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-sm border border-yellow-400/50 rounded-2xl p-10 text-center">
                <div className="max-w-3xl mx-auto">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-6">
                    <span className="text-3xl">🚀</span>
                  </div>
                  <p className="text-2xl md:text-3xl text-white font-bold leading-relaxed">
                    This isn't a fantasy. This is the 
                    <span className="text-yellow-300"> new standard</span> for thousands of marketers.
                  </p>
                  <p className="text-3xl md:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 font-black mt-4">
                    And today, it can be YOUR reality.
                  </p>
                </div>
          </div>
        </div>
      </div>

          {/* Action prompt */}
          <div className="text-center animate-fade-in-up animation-delay-800">
            <p className="text-xl text-gray-300 mb-8">
              Ready to transform your marketing workflow forever?
            </p>
            <CTASection 
              className=""
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
            />
          </div>
        </div>
      </div>

      {/* MONEY BACK GUARANTEE - Premium Trust Design */}
      <div className="relative py-32 overflow-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #10b981 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Floating shield elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-64 h-64 bg-green-400/10 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-teal-400/10 rounded-full filter blur-[100px]"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Trust badge */}
          <div className="flex justify-center mb-10 animate-fade-in-down">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-teal-400 rounded-full blur opacity-50 animate-pulse"></div>
              <div className="relative inline-flex items-center px-8 py-4 bg-white rounded-full shadow-xl">
                <svg className="w-8 h-8 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg font-bold text-gray-900 uppercase tracking-wider">100% Money-Back Guarantee</span>
              </div>
            </div>
          </div>
          
          {/* Main headline */}
          <h2 className="text-5xl md:text-7xl font-black text-center mb-6 leading-tight animate-fade-in-up">
            <span className="text-gray-900">Try It For 30 Days,</span>
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600">
              100% Risk-Free
            </span>
          </h2>
          
          <p className="text-2xl text-gray-700 text-center mb-16 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
            Our "Look More Professional" Money-Back Guarantee means you either 
            <span className="font-semibold text-green-600"> love the results</span>, or you get a 
            <span className="font-semibold text-green-600"> full refund</span>. It's that simple.
          </p>
          
          {/* Guarantee card */}
          <div className="relative max-w-4xl mx-auto animate-scale-in animation-delay-400">
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-green-400 via-teal-400 to-cyan-400 rounded-3xl blur-xl opacity-30"></div>
            
            {/* Main card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-green-500 to-teal-500 p-8 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                  <span className="text-4xl">🛡️</span>
                </div>
                <h3 className="text-3xl font-bold text-white">Your Investment is Fully Protected</h3>
              </div>
              
              {/* Content */}
              <div className="p-10 space-y-8">
                {/* Step 1 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      1
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Secure Your Access Today</h4>
                    <p className="text-lg text-gray-700">
                      Get instant access to BrandScapes AI and use it to create as many mockups as you want for the next 30 days. 
                      <span className="font-semibold text-green-600"> No limits, no restrictions.</span>
                    </p>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      2
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Test It Risk-Free</h4>
                    <p className="text-lg text-gray-700">
                      If you don't agree that it's the 
                      <span className="font-semibold text-teal-600"> fastest and easiest way</span> to create stunning visuals... 
                      If it doesn't make your brand look 
                      <span className="font-semibold text-teal-600"> more professional and valuable</span>...
                    </p>
                  </div>
                </div>
                
                {/* Step 3 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      3
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Get a Full Refund</h4>
                    <p className="text-lg text-gray-700">
                      Simply send our support team an email, and we'll refund 
                      <span className="font-semibold text-green-600"> 100% of your payment</span>. 
                      No hoops to jump through, no questions asked, no hard feelings.
              </p>
            </div>
          </div>
                
                {/* Bottom line */}
                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-8">
                  <div className="flex items-start gap-6">
                    {/* Money Back Badge */}
                    <div className="flex-shrink-0">
                      <img 
                        src="/money-back.png" 
                        alt="30 Day Money Back Guarantee" 
                        className="w-28 h-28 object-contain filter drop-shadow-lg"
                      />
                    </div>
                    
                    {/* Text Content */}
                    <div className="flex-1 text-left">
                      <p className="text-2xl font-bold text-gray-900 mb-2">
                        The Bottom Line:
                      </p>
                      <p className="text-xl text-gray-700">
                        You either get a tool that 
                        <span className="font-bold text-green-600"> transforms your marketing forever</span>, 
                        or you get 
                        <span className="font-bold text-green-600"> your money back</span>.
                      </p>
                      <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600 mt-4">
                        There is ZERO risk to you.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-12 text-gray-600 animate-fade-in-up animation-delay-600">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>30-Day Guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Secure Checkout</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
              </svg>
              <span>24/7 Support</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Trusted by 10,000+</span>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ SECTION - Premium Interactive Design */}
      <div className="relative py-32 overflow-hidden">
        {/* Light gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
        
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #6366f1 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-200/20 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-purple-200/20 rounded-full filter blur-[100px]"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4">
          {/* Section header */}
          <div className="text-center mb-16 animate-fade-in-down">
            {/* Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white rounded-full shadow-lg mb-8">
              <svg className="w-5 h-5 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-gray-900 uppercase tracking-wider">Frequently Asked Questions</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 leading-tight animate-fade-in-up">
              Your Questions, 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Answered</span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Click on any question below to reveal the answer. We've covered everything you need to know before getting started.
            </p>
          </div>
          
          {/* FAQ Accordion */}
          <div className="space-y-4 animate-fade-in-up animation-delay-400">
            {[
              {
                q: "Is this really a one-time payment for unlimited mockups?",
                a: "Yes! Unlike other platforms that charge monthly fees or per mockup, BrandScapes AI offers lifetime access for just $37 during this special launch. Create unlimited mockups forever with no recurring charges.",
                icon: "💰"
              },
              {
                q: "What are the usage rights?",
                a: "You have full commercial rights. Use the mockups you create for your own business, your clients, and in any marketing materials. There are no restrictions on commercial usage, and you own everything you create.",
                icon: "📜"
              },
              {
                q: "Is there any software to install?",
                a: "No installation required! BrandScapes AI is 100% cloud-based. Access it from any device with an internet connection - Mac, PC, tablets, even your phone. Your work is automatically saved and synced across all devices.",
                icon: "☁️"
              },
              {
                q: "What if I'm not a designer?",
                a: "Perfect! The platform is built specifically for non-designers. Zero design skills required. If you can upload an image and click a button, you can create professional mockups. Our AI handles all the complex lighting, shadows, and perspective work automatically.",
                icon: "🎨"
              },
              {
                q: "How fast can I really create mockups?",
                a: "Most mockups are generated in under 30 seconds. Upload your design, choose a template, and click generate. That's it! You can create dozens of variations in minutes, not hours.",
                icon: "⚡"
              },
              {
                q: "What is your refund policy?",
                a: "We offer a 30-day no-questions-asked money-back guarantee. If you're not completely satisfied for any reason, simply email our support team and we'll refund 100% of your purchase. No hoops to jump through.",
                icon: "🛡️"
              }
            ].map((faq, index) => (
              <div key={index} className="relative group">
                {/* Glow effect on hover */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                
                {/* FAQ Item */}
                <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center text-2xl">
                        {faq.icon}
                      </div>
                      
                      {/* Question */}
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 pr-4">
                        {faq.q}
                      </h3>
                    </div>
                    
                    {/* Expand/Collapse Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center transform transition-transform duration-200 ${openFAQ === index ? 'rotate-180' : ''}`}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {/* Answer */}
                  <div className={`overflow-hidden transition-all duration-200 ease-in-out ${openFAQ === index ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-8 pb-6">
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-gray-700 leading-relaxed">
                          {faq.a}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Bottom CTA - Professional Trust Design */}
          <div className="mt-20 animate-fade-in-up animation-delay-600">
            {/* Trust Box */}
            <div className="relative max-w-3xl mx-auto">
              {/* Subtle glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-200 via-purple-200 to-blue-200 rounded-3xl blur-2xl opacity-20"></div>
              
              {/* Main container */}
              <div className="relative bg-white rounded-3xl shadow-xl p-10">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl mb-4">
                    <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
        </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Still Have Questions?
                  </h3>
                  <p className="text-lg text-gray-600">
                    We're here to help you make the right decision
                  </p>
      </div>

                {/* Two-column layout for support options */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Email Support */}
                  <a 
                    href="mailto:support@brandscapes.ai" 
                    className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 hover:border-blue-300 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">Email Support</h4>
                        <p className="text-sm text-gray-600">Get answers within 24 hours</p>
                        <p className="text-sm font-medium text-blue-600 mt-2 group-hover:text-blue-700">
                          support@brandscapes.ai →
                        </p>
                      </div>
                    </div>
                  </a>
                  
                  {/* Live Chat (coming soon) */}
                  <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        Coming Soon
                      </span>
            </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                        </svg>
            </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">Live Chat</h4>
                        <p className="text-sm text-gray-600">Instant answers from our team</p>
                        <p className="text-sm text-gray-500 mt-2">Available soon</p>
            </div>
                    </div>
            </div>
          </div>
          
                {/* Divider */}
                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-white text-sm text-gray-500">or start risk-free</span>
        </div>
      </div>

                {/* Main CTA */}
                <div className="text-center">
                  <CTASection 
                    className=""
                    showHeadline={true}
                    showRating={true}
                    showPaymentImage={true}
                    useDarkPaymentImage={true}
                    darkMode={true}   
                  />
                  
                  {/* Trust badges */}
                  <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      <span>Secure Checkout</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>30-Day Guarantee</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* COUNTDOWN TIMER URGENCY - Premium Scarcity Design */}
      <div className="relative py-24 overflow-hidden">
        {/* Gradient background with depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-800 to-rose-900"></div>
        
        {/* Overlay patterns for texture */}
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        
        {/* Animated elements */}
        <div className="absolute inset-0">
          {/* Pulsing glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-500/20 rounded-full filter blur-[100px] animate-pulse"></div>
          
          {/* Rotating lines */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
            <div className="absolute inset-0 opacity-5" style={{
              background: `conic-gradient(from 0deg, transparent, rgba(255,255,255,0.1), transparent)`,
              animation: 'spin 20s linear infinite'
            }}></div>
          </div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Urgency Badge */}
          <div className="flex justify-center mb-8 animate-fade-in-down">
            <div className="relative">
              <div className="absolute -inset-1 bg-white/30 rounded-full blur animate-pulse"></div>
              <div className="relative inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full animate-ping"></div>
                  <span className="text-sm font-medium text-white uppercase tracking-wider">Limited Time Offer</span>
                  <div className="w-2 h-2 bg-red-400 rounded-full animate-ping animation-delay-500"></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Headline */}
          <h3 className="text-4xl md:text-6xl font-black text-center text-white mb-6 leading-tight animate-fade-in-up">
            Founder's Pricing 
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300">
              Expires Soon!
            </span>
          </h3>
          
          <p className="text-xl md:text-2xl text-red-100 text-center mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
            After this timer hits zero, the price jumps to 
            <span className="font-bold text-white"> $67/month forever</span>. 
            Lock in your lifetime access now at just 
            <span className="font-bold text-yellow-300"> $37 once</span>.
          </p>
          
          {/* Timer Container */}
          <div className="relative max-w-4xl mx-auto mb-12">
            {/* Timer glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-600/20 via-red-600/20 to-orange-600/20 rounded-3xl blur-2xl"></div>
            
            {/* Timer boxes */}
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4 animate-scale-in animation-delay-400">
              {/* Days */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-rose-700 rounded-2xl opacity-90"></div>
                <div className="relative bg-gradient-to-br from-red-700/90 to-rose-800/90 backdrop-blur-sm rounded-2xl p-6 border border-red-500/30 text-center transform group-hover:scale-105 transition-transform duration-200">
                  <div className="text-5xl md:text-6xl font-black text-white mb-2 font-mono">
                    {timeLeft.days.toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm font-medium text-red-200 uppercase tracking-wider">Days</div>
                </div>
              </div>
              
              {/* Hours */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-rose-700 rounded-2xl opacity-90"></div>
                <div className="relative bg-gradient-to-br from-red-700/90 to-rose-800/90 backdrop-blur-sm rounded-2xl p-6 border border-red-500/30 text-center transform group-hover:scale-105 transition-transform duration-200">
                  <div className="text-5xl md:text-6xl font-black text-white mb-2 font-mono">
                    {timeLeft.hours.toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm font-medium text-red-200 uppercase tracking-wider">Hours</div>
                </div>
              </div>
              
              {/* Minutes */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-rose-700 rounded-2xl opacity-90"></div>
                <div className="relative bg-gradient-to-br from-red-700/90 to-rose-800/90 backdrop-blur-sm rounded-2xl p-6 border border-red-500/30 text-center transform group-hover:scale-105 transition-transform duration-200">
                  <div className="text-5xl md:text-6xl font-black text-white mb-2 font-mono">
                    {timeLeft.minutes.toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm font-medium text-red-200 uppercase tracking-wider">Minutes</div>
                </div>
              </div>
              
              {/* Seconds */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-rose-700 rounded-2xl opacity-90 animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-red-700/90 to-rose-800/90 backdrop-blur-sm rounded-2xl p-6 border border-red-500/30 text-center transform group-hover:scale-105 transition-transform duration-200">
                  <div className="text-5xl md:text-6xl font-black text-white mb-2 font-mono">
                    {timeLeft.seconds.toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm font-medium text-red-200 uppercase tracking-wider">Seconds</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Urgency messages */}
          <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-3xl mx-auto">
            <div className="text-center animate-fade-in-up animation-delay-600">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-full mb-3">
                <svg className="w-6 h-6 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm text-red-100">Price increases to</p>
              <p className="text-lg font-bold text-white">$67/month</p>
            </div>
            
            <div className="text-center animate-fade-in-up animation-delay-700">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-full mb-3">
                <svg className="w-6 h-6 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm text-red-100">Save $767/year</p>
              <p className="text-lg font-bold text-white">Act Now</p>
            </div>
            
            <div className="text-center animate-fade-in-up animation-delay-800">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-full mb-3">
                <svg className="w-6 h-6 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm text-red-100">Lifetime access</p>
              <p className="text-lg font-bold text-white">Just $37</p>
            </div>
          </div>
          
          {/* CTA with enhanced styling */}
          <div className="text-center animate-fade-in-up animation-delay-1000">
            <CTASection 
              className=""
              showHeadline={true}
              showRating={true}
              showPaymentImage={true}
            />
            
            {/* Trust element */}
            <p className="text-sm text-red-200 mt-4">
              <svg className="w-4 h-4 inline-block mr-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Secure checkout • 30-day money-back guarantee
            </p>
          </div>
        </div>
      </div>

      {/* FINAL CTA SECTION - Premium Decision Point Design */}
      <div className="relative py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        {/* Dynamic background elements */}
        <div className="absolute inset-0">
          {/* Animated gradient mesh */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-[128px] animate-float"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-600 rounded-full mix-blend-multiply filter blur-[128px] animate-float animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-600 rounded-full mix-blend-multiply filter blur-[128px] animate-float animation-delay-4000"></div>
          </div>
          
          {/* Radial gradient overlay */}
          <div className="absolute inset-0 bg-radial-gradient from-transparent via-gray-900/50 to-gray-900"></div>
          
          {/* Animated particles */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => (
              <div
                key={`cta-particle-${i}`}
                className="absolute w-1 h-1 bg-white rounded-full animate-float-up"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  opacity: Math.random() * 0.5 + 0.2,
                  animationDelay: `${Math.random() * 10}s`,
                  animationDuration: `${15 + Math.random() * 10}s`
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Urgency Banner */}
          <div className="flex justify-center mb-12 animate-fade-in-down">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-orange-600 rounded-full blur opacity-75 animate-pulse"></div>
              <div className="relative inline-flex items-center px-6 py-3 bg-black border border-red-500 rounded-full">
                <svg className="w-5 h-5 text-red-500 mr-2 animate-ping" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-red-500 uppercase tracking-wider">Decision Time</span>
              </div>
            </div>
          </div>
          
          {/* Main Headline */}
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-center mb-12 leading-tight animate-fade-in-up">
            <span className="text-white">Don't Let a </span>
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-pink-500">
                $37 Decision
              </span>
              <span className="absolute -bottom-2 left-0 right-0 h-4 bg-gradient-to-r from-orange-400/20 via-red-500/20 to-pink-500/20 blur-xl"></span>
            </span>
            <span className="block text-white mt-4">
              Hold Back Your
            </span>
            <span className="block relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 animate-gradient-x">
                $10,000+ Potential
              </span>
              <span className="absolute -bottom-2 left-0 right-0 h-4 bg-gradient-to-r from-green-400/20 via-emerald-400/20 to-teal-400/20 blur-xl"></span>
            </span>
          </h2>
          
          {/* Crossroads Visual */}
          <div className="relative max-w-4xl mx-auto mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Path 1: Continue Struggling */}
              <div className="relative group animate-fade-in-left animation-delay-200">
                <div className="absolute -inset-1 bg-gradient-to-r from-gray-600 to-gray-700 rounded-2xl blur opacity-50 group-hover:opacity-75 transition duration-300"></div>
                <div className="relative bg-gray-900 border border-gray-700 rounded-2xl p-8 h-full">
                  <div className="text-6xl mb-4">😔</div>
                  <h3 className="text-2xl font-bold text-gray-300 mb-4">Path 1: Status Quo</h3>
                  <ul className="space-y-3 text-gray-400">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">✗</span>
                      Keep losing sales to competitors with better visuals
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">✗</span>
                      Continue paying $50-500 per mockup to designers
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">✗</span>
                      Watch your ideas gather dust, unexpressed
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">✗</span>
                      Feel that constant visual marketing anxiety
                    </li>
                  </ul>
                  <div className="mt-6 pt-6 border-t border-gray-800">
                    <p className="text-sm text-gray-500">Cost: Immeasurable lost opportunity</p>
                  </div>
                </div>
              </div>
              
              {/* Path 2: Transform Today */}
              <div className="relative group animate-fade-in-right animation-delay-400">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300 animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-gray-900 to-black border border-green-500/50 rounded-2xl p-8 h-full">
                  <div className="text-6xl mb-4">🚀</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Path 2: Transform Now</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Create unlimited premium mockups in seconds
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Save $1000s on design costs immediately
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Launch products with confidence & speed
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Join 10,000+ successful marketers
                    </li>
                  </ul>
                  <div className="mt-6 pt-6 border-t border-green-900/50">
                    <p className="text-sm text-green-400 font-semibold">Investment: Just $37 once (Save $767/year)</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* VS Badge in Center */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:block">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-orange-600 to-pink-600 rounded-full blur-lg opacity-75 animate-pulse"></div>
                <div className="relative w-20 h-20 bg-black border-2 border-white rounded-full flex items-center justify-center">
                  <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">VS</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Powerful Statement */}
          <div className="relative max-w-3xl mx-auto mb-16">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-30"></div>
              <div className="relative bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-10 text-center">
                <p className="text-2xl md:text-3xl text-white font-light leading-relaxed mb-6">
                  "The <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">most expensive</span> decision 
                  isn't the <span className="font-semibold">$37</span> you invest today...
                </p>
                <p className="text-3xl md:text-4xl text-white font-bold">
                  It's the <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-400 animate-gradient-x">
                    thousands you'll lose
                  </span> by waiting."
                </p>
              </div>
            </div>
          </div>
          
          {/* Final CTA */}
          <div className="text-center">
            {/* Timer reminder */}
            <div className="inline-flex items-center px-6 py-3 bg-red-900/30 border border-red-500/50 rounded-full mb-8 backdrop-blur-sm animate-fade-in-up animation-delay-600">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-red-300 font-medium">Launch pricing expires when timer hits zero</span>
            </div>
            
            {/* Main CTA Button */}
            <div className="mb-8">
              <CTASection 
                showHeadline={true}
                showRating={true}
                showPaymentImage={true}
              />
          </div>
            
            {/* Trust Elements */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-gray-400 animate-fade-in-up animation-delay-1000">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>100% Risk-Free Purchase</span>
        </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
                <span>30-Day Money-Back Guarantee</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span>Join 10,000+ Happy Users</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER - Professional Design */}
      <footer className="relative bg-gray-900 text-gray-300">
      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
      
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Logo and Company Info */}
        <div className="text-center mb-12">
          <img 
            src="/clawbrand_logo_color.png" 
            alt="ClawBrand AI" 
            className="h-16 mx-auto mb-6"
          />
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Transform your marketing with AI-powered mockups. Create stunning visuals in seconds, not hours.
            </p>
          </div>
          
        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12 text-sm">
          <a href="/support" className="hover:text-white transition-colors duration-200">Support</a>
          <span className="text-gray-600">•</span>
          <a href="/privacy" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
          <span className="text-gray-600">•</span>
          <a href="/refund" className="hover:text-white transition-colors duration-200">Refund Policy</a>
          <span className="text-gray-600">•</span>
          <a href="/cookies" className="hover:text-white transition-colors duration-200">Cookie Policy</a>
          <span className="text-gray-600">•</span>
          <a href="/terms" className="hover:text-white transition-colors duration-200">Terms of Service</a>
          <span className="text-gray-600">•</span>
          <a href="/disclaimer" className="hover:text-white transition-colors duration-200">Disclaimer</a>
        </div>
        
        {/* Disclaimers */}
        <div className="space-y-6 text-xs text-gray-500 max-w-4xl mx-auto">
          {/* Facebook Disclaimer */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">Facebook Disclaimer</h4>
            <p className="leading-relaxed">
              In no way are we associated with Facebook. Once you leave Facebook the responsibility is no longer on any Instagram owned entity.
              Your information and privacy is totally secure and will NEVER be shared. Facebook is in NO WAY associated with this website or anything here.
            </p>
          </div>
          
          {/* General Disclaimer */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">General Disclaimer</h4>
            <p className="leading-relaxed">
              Please note that this product does not provide any guarantee of income or success. The results achieved by the product owner or any other individuals mentioned are not indicative of future success or earnings. This website is not affiliated with FaceBook or any of its associated entities. Once you navigate away from FaceBook, the responsibility for the content and its usage lies solely with the user. All content on this website, including but not limited to text, images, and multimedia, is protected by copyright law and the Digital Millennium Copyright Act. Unauthorized copying, duplication, modification, or theft, whether intentional or unintentional, is strictly prohibited. Violators will be prosecuted to the fullest extent of the law. We want to clarify that JVZoo serves as the retailer for the products featured on this site. JVZoo® is a registered trademark of BBC Systems Inc., a Florida corporation located at 1809 E. Broadway Street, Suite 125, Oviedo, FL 32765, USA, and is used with permission. The role of JVZoo as a retailer does not constitute an endorsement, approval, or review of these products or any claims, statements, or opinions used in their promotion.
            </p>
        </div>
      </div>
        
        {/* Copyright */}
        <div className="text-center mt-12 pt-8 border-t border-gray-800">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
    </div>
  );
};

export default Special;